/**
 * A BASE DE CONHECIMENTO DO VOE · as categorias e os artigos do produto de operacao aerea.
 *
 * ===========================================================================
 * POR QUE ESTE ARQUIVO EXISTE, e o buraco que ele fecha
 * ===========================================================================
 * MEDIDO EM 07/09/2026, 19:35: `fonte-memoria.ts:66` era `Object.freeze({})`. O acervo em
 * memoria estava VAZIO, e a unica chave que existira (`psicosocial`) saiu junto com aquele
 * produto naquele mesmo dia.
 *
 * A CONSEQUENCIA FOI MEDIDA NA TELA, e nao deduzida do codigo: com o VOE no ar na 5179 e
 * login feito com a conta de trabalho, a Central de Ajuda desenhava (a suite de
 * conformidade prova isso, caso "a Central de Ajuda desenha ao navegar pelo item do menu"),
 * e mostrava ZERO categorias. O rodape da tela dizia "A fonte de dados ainda nao esta
 * configurada".
 *
 * Ou seja: a moldura estava certa e o acervo estava vazio. Este arquivo e o acervo.
 *
 * ===========================================================================
 * O CONTEUDO SAI DO PRODUTO, E NAO DA CABECA DE QUEM ESCREVE
 * ===========================================================================
 * Cada artigo foi escrito a partir do codigo das telas de `apps/voe/src/telas/`, dos
 * modulos de `apps/voe/src/dominio/`, do manifesto `voe.config.ts` e das regras do banco
 * registradas em `docs/voe/O-QUE-O-VOE-ENTREGA.md`.
 *
 * Ajuda inventada e PIOR que ajuda nenhuma: ela ensina um sistema que nao existe, e quem
 * seguir o passo escrito aqui vai encontrar outra tela. A pessoa entao desconfia da base
 * inteira, inclusive da parte correta.
 *
 * ===========================================================================
 * A REGRA QUE MAIS PESOU NA ESCRITA
 * ===========================================================================
 * NENHUMA TELA DESTE PRODUTO DECIDE PELO COMANDANTE. Por isso nenhum artigo daqui diz que
 * o sistema "libera o voo", "aprova a aeronave" ou "autoriza a decolagem". Os artigos dizem
 * o que a tela MOSTRA e o que ela RECUSA, que e o que ela de fato faz. Onde a tela recusa,
 * o artigo diz o que fazer para resolver.
 *
 * ===========================================================================
 * O QUE ESTE ARQUIVO NAO E
 * ===========================================================================
 * Ele NAO e o caminho de producao. Em producao a Central le `services/knowledge` por HTTP,
 * decidido em `packages/chassi/src/fonte-de-ajuda.ts:91-103` conforme `VITE_CX_API_KNOWLEDGE`
 * esteja definida. Este acervo e o que a tela mostra quando o servico nao esta configurado,
 * e esse modo degradado e DECLARADO na tela, nunca escondido.
 */

import type { CategoriaAjuda } from '../central.js';
import type { AcervoDaBase, ArtigoDaBase } from '../fonte-memoria.js';

/**
 * AS CATEGORIAS, na ordem em que a pessoa encontra o assunto.
 *
 * A ORDEM NAO E ALFABETICA, e a escolha e a mesma do menu do produto: ela segue o CICLO DO
 * VOO, porque e assim que quem opera pensa. Ordenar por alfabeto poria "Conformidade" antes
 * de "Primeiros passos", e quem abre a ajuda pela primeira vez cairia no assunto mais
 * tecnico do produto.
 *
 * O CAMPO `artigos` NAO APARECE AQUI, e a ausencia e deliberada: ele e DERIVADO no fim deste
 * arquivo, contando os artigos de cada categoria. Digitado a mao, ele divergiria no primeiro
 * artigo novo, e a tela mostraria "8 artigos" numa categoria com 9. Este repositorio ja
 * registrou QUATRO vezes o defeito de numero escrito a mao em documento de controle, e a
 * licao foi sempre a mesma: numero vem de contagem feita na hora, ou nao vem.
 */
const CATEGORIAS_SEM_CONTAGEM: readonly Omit<CategoriaAjuda, 'artigos'>[] = [
  { id: 'primeiros-passos', nome: 'Primeiros passos' },
  { id: 'voar', nome: 'Voar: o despacho' },
  { id: 'painel', nome: 'Painel e indicadores' },
  { id: 'frota', nome: 'Frota e aeronaves' },
  { id: 'tripulacao', nome: 'Tripulação e licenças' },
  { id: 'meteorologia', nome: 'Meteorologia' },
  { id: 'notam', nome: 'NOTAM e aeródromos' },
  { id: 'voos', nome: 'Voos e escala' },
  { id: 'diario', nome: 'Diário de bordo' },
  { id: 'seguranca', nome: 'Segurança operacional' },
  { id: 'conformidade', nome: 'Conformidade' },
  { id: 'normas', nome: 'Normas' },
  { id: 'ia', nome: 'IA: o que ela faz e o que não faz' },
];

/* ===== bloco 1-inicio ===== */
const ARTIGOS_1: ArtigoDaBase[] = [
  // -- Primeiros passos ---------------------------------------------------------------
  {
    id: 'voe-pp-01',
    titulo: 'O que o VOE faz, e o que ele não decide',
    resumo: 'O sistema mostra o que a norma e o cadastro dizem. Quem decide operar é o comandante.',
    marcadores: ['visão geral', 'responsabilidade', 'comandante', 'decisão'],
    categoriaId: 'primeiros-passos',
  },
  {
    id: 'voe-pp-02',
    titulo: 'Como navegar: o menu lateral e os quatro atalhos do celular',
    resumo: 'Os 14 itens do menu, na ordem do ciclo do voo, e os quatro que ficam na barra do celular.',
    marcadores: ['menu', 'navegação', 'celular', 'atalhos'],
    categoriaId: 'primeiros-passos',
  },
  {
    id: 'voe-pp-03',
    titulo: 'A ordem do dia: do cadastro do voo até a decolagem',
    resumo: 'A sequência que evita retrabalho: frota, tripulação, voo, escala e só então o despacho.',
    marcadores: ['fluxo', 'rotina', 'sequência', 'começar'],
    categoriaId: 'primeiros-passos',
  },
  {
    id: 'voe-pp-04',
    titulo: 'Traço, lista vazia e fonte fora do ar: como ler a ausência de dado',
    resumo: 'Três situações parecidas na tela e diferentes na prática. O que cada uma pede de você.',
    marcadores: ['traço', 'vazio', 'erro', 'indisponível'],
    categoriaId: 'primeiros-passos',
  },
  {
    id: 'voe-pp-05',
    titulo: 'Trocar o idioma e o tema da tela',
    resumo: 'Português e inglês, tema claro e escuro, sempre no mesmo canto: alto da tela, à direita.',
    marcadores: ['idioma', 'tema', 'claro', 'escuro'],
    categoriaId: 'primeiros-passos',
  },

  // -- Voar: o despacho ---------------------------------------------------------------
  {
    id: 'voe-vr-01',
    titulo: 'Despachar um voo: o caminho completo na tela Voar',
    resumo: 'Escolher o voo, conferir condições, impedimentos, MEL e checklist, e registrar a decolagem.',
    marcadores: ['despacho', 'voar', 'decolagem', 'pátio'],
    categoriaId: 'voar',
  },
  {
    id: 'voe-vr-02',
    titulo: 'Impede ou Atenção: como ler a lista de impedimentos',
    resumo: 'Uma linha por impedimento, com o que venceu, onde resolver e até quando valia.',
    marcadores: ['impedimento', 'vencimento', 'bloqueio', 'alerta'],
    categoriaId: 'voar',
  },
  {
    id: 'voe-vr-03',
    titulo: 'O botão de registrar decolagem está apagado: o que fazer',
    resumo: 'A tela lista os motivos acima do botão. Cada motivo tem um lugar certo para resolver.',
    marcadores: ['botão desabilitado', 'não decola', 'motivos', 'resolver'],
    categoriaId: 'voar',
  },
  {
    id: 'voe-vr-04',
    titulo: 'MEL: equipamento inoperante com diferimento em vigor',
    resumo: 'O que a tabela de MEL mostra, o que a categoria significa e o que é restrição operacional.',
    marcadores: ['MEL', 'diferimento', 'inoperante', 'restrição'],
    categoriaId: 'voar',
  },
  {
    id: 'voe-vr-05',
    titulo: 'Checklist do voo: item obrigatório e item comum',
    resumo: 'O item marcado com asterisco impede a decolagem enquanto não for conferido.',
    marcadores: ['checklist', 'obrigatório', 'conferir', 'pendente'],
    categoriaId: 'voar',
  },
  {
    id: 'voe-vr-06',
    titulo: 'Registrar a decolagem: a confirmação e o que não tem desfazer',
    resumo: 'O registro lança o voo no diário de bordo, que é assinado e imutável. Não há como apagar.',
    marcadores: ['decolagem', 'confirmação', 'diário', 'irreversível'],
    categoriaId: 'voar',
  },

  // -- Painel e indicadores -----------------------------------------------------------
  {
    id: 'voe-pn-01',
    titulo: 'O que o painel mostra',
    resumo: 'Voos de hoje, alertas abertos, críticos, vencidos e o que vence nos próximos dias.',
    marcadores: ['painel', 'indicadores', 'alertas', 'visão geral'],
    categoriaId: 'painel',
  },
  {
    id: 'voe-pn-02',
    titulo: 'Filtrar clicando no gráfico, e os recortes que somam',
    resumo: 'Clique numa barra para filtrar, clique de novo para desfazer. Dois filtros somam, não trocam.',
    marcadores: ['filtro', 'gráfico', 'recorte', 'clique'],
    categoriaId: 'painel',
  },
  {
    id: 'voe-pn-03',
    titulo: 'O que vence: vencido, hoje, próximo e futuro',
    resumo: 'A lista de vencimentos separa o que já passou do que ainda dá tempo de renovar.',
    marcadores: ['vencimento', 'prazo', 'renovação', 'validade'],
    categoriaId: 'painel',
  },
  {
    id: 'voe-pn-04',
    titulo: 'Por que alguns números do painel não são clicáveis',
    resumo: 'Contagem filtra a tela. Taxa e média não filtram nada, então não viram botão.',
    marcadores: ['clicável', 'métrica', 'taxa', 'contagem'],
    categoriaId: 'painel',
  },
  {
    id: 'voe-pn-05',
    titulo: 'Painel sem alerta nenhum não é painel quebrado',
    resumo: 'A tela diz quando a consulta respondeu e não há nada aberto, e isso é boa notícia.',
    marcadores: ['sem alerta', 'vazio', 'consulta', 'boa notícia'],
    categoriaId: 'painel',
  },

  // -- Voos e escala ------------------------------------------------------------------
  {
    id: 'voe-vo-01',
    titulo: 'Cadastrar um voo',
    resumo: 'Número, aeronave, trecho, horários, regra de voo, regime e a observação obrigatória.',
    marcadores: ['cadastro', 'novo voo', 'formulário', 'planejamento'],
    categoriaId: 'voos',
  },
  {
    id: 'voe-vo-02',
    titulo: 'Escalar a tripulação de um voo',
    resumo: 'A escala fica no detalhe do voo. Sem comandante escalado o voo não é registrado como decolado.',
    marcadores: ['escala', 'tripulação', 'comandante', 'copiloto'],
    categoriaId: 'voos',
  },
  {
    id: 'voe-vo-03',
    titulo: 'Situação e status do voo: duas perguntas diferentes',
    resumo: 'O status diz onde o voo está no ciclo. A situação diz se ele precisa de atenção agora.',
    marcadores: ['situação', 'status', 'planejado', 'concluído'],
    categoriaId: 'voos',
  },
  {
    id: 'voe-vo-04',
    titulo: 'Ver o que falta para um voo decolar antes de ir ao pátio',
    resumo: 'O detalhe do voo nomeia o que falta enquanto ainda dá tempo de resolver.',
    marcadores: ['pendência', 'antecipar', 'detalhe', 'falta'],
    categoriaId: 'voos',
  },
  {
    id: 'voe-vo-05',
    titulo: 'Voos no celular: lista e detalhe se revezam',
    resumo: 'No telefone aparece uma coisa por vez, com o botão de voltar para a lista.',
    marcadores: ['celular', 'lista', 'detalhe', 'voltar'],
    categoriaId: 'voos',
  },
];

const CORPOS_1: Record<string, string> = {
  // -- Primeiros passos ---------------------------------------------------------------
  'voe-pp-01': `## O que o VOE faz, e o que ele não decide

O VOE reúne, numa tela só, o que a sua operação precisa saber antes de um voo sair: a aeronave e os documentos dela, a tripulação escalada e as habilitações de cada um, os itens de equipamento inoperante com diferimento em vigor, e o checklist daquele voo.

O que o sistema faz é mostrar fato e recusar registro irregular. Ele apresenta o que o cadastro e a norma dizem, item por item, com o prazo de cada um, e impede que uma decolagem seja lançada quando falta algo que a própria operação declarou obrigatório.

O que o sistema não faz é decidir pela pessoa. Ele não libera voo, não aprova aeronave e não autoriza operação. A decisão de operar é do comandante, e continua sendo dele mesmo quando a tela não encontra nenhum impedimento.

Isso vale inclusive para a lista de impedimentos vazia. Ela significa uma coisa só: nenhum impedimento foi encontrado pelos critérios que o sistema consegue verificar. Existem verificações que ficam de fora por decisão registrada, como a inspeção por horas, que depende do total de horas da célula naquele momento. Ler a lista vazia como permissão é o erro mais caro que se pode cometer com este produto.

A inteligência artificial deste produto não opina sobre despacho. A tela de despacho não consulta modelo nenhum: os impedimentos vêm de regra escrita, conferida contra a norma, e o resultado é sempre a mesma lista para o mesmo cadastro.`,

  'voe-pp-02': `## Como navegar: o menu lateral e os quatro atalhos do celular

O menu do VOE tem 14 itens, e a ordem deles não é alfabética: ela segue o ciclo do voo, do despacho ao registro. Começa em Voar, que é o gesto do dia, e termina nos itens de sistema.

Na primeira parte estão os 12 itens de operação, nesta ordem: Voar, Painel, Frota, Tripulação, Meteorologia, NOTAM e aeródromos, Mapa, Voos, Diário de bordo, Segurança, Conformidade e Normas. Abaixo de uma divisória ficam os 2 itens de sistema: Ajuda e Chaves e extrato de IA.

Voar aparece em destaque porque é o item que se usa todo dia, com a aeronave no pátio e alguém esperando. Os outros são consulta e cadastro, e o destaque existe para você não atravessar formulário até chegar no que interessa.

### No celular

No telefone o menu completo abre por um toque, e quatro itens ficam sempre visíveis numa barra na parte de baixo da tela: Voar, Painel, Tripulação e Voos. São os quatro que alguém acessa em pé, no pátio, sem tempo de procurar. Os demais continuam disponíveis no menu completo, e nenhum item some no celular.

Se você trabalha no telefone e sente falta de um item, ele está no menu completo, e não foi removido. A barra de baixo é atalho, e não a lista inteira.`,

  'voe-pp-03': `## A ordem do dia: do cadastro do voo até a decolagem

Existe uma sequência que evita a maior parte do retrabalho. Ela não é obrigatória pelo sistema, mas fora dela você vai esbarrar em recusas que poderiam ter sido resolvidas antes.

### A sequência

1. **Frota.** A aeronave precisa estar cadastrada antes do voo. Se ela não estiver, o formulário de voo mostra "Nenhuma aeronave cadastrada. Cadastre a frota antes de lançar o voo.", e você para no meio do lançamento.
2. **Tripulação.** Os tripulantes precisam existir no cadastro para poderem ser escalados. Habilitação e licença ficam registradas com a validade de cada uma.
3. **Voos.** Cadastre o voo com número, aeronave, trecho, horários, regra de voo e regime.
4. **Escala.** Ainda no detalhe do voo, escale a tripulação. O comandante é o que o sistema exige para registrar a decolagem.
5. **Voar.** No dia, com a aeronave no pátio, abra Voar, escolha o voo e confira o que a tela mostra.

### Por que nesta ordem

Cada passo depende do anterior. Cadastrar o voo sem aeronave na frota deixa o voo sem matrícula, e isso vira um impedimento na hora do despacho, quando resolver custa mais. Escalar a tripulação um dia antes custa dois minutos; escalar com o motor ligado custa o atraso inteiro.

O sistema mostra a pendência tanto no detalhe do voo quanto na tela de despacho, exatamente para você resolver na véspera e não no pátio.`,

  'voe-pp-04': `## Traço, lista vazia e fonte fora do ar: como ler a ausência de dado

Três situações se parecem na tela e pedem coisas diferentes de você. Confundi-las faz alguém procurar defeito onde não há, ou confiar num dado que não existe.

### O traço

Um traço no lugar de um valor significa que aquela informação não se aplica ou não foi cadastrada. No bloco de condições do voo, um traço em Comandante quer dizer que ninguém foi escalado ainda. Na coluna de vencimento de um impedimento, o traço quer dizer que aquele impedimento não é de prazo, e sim de estado, como uma aeronave em manutenção.

O traço nunca é preenchido com um valor provável. Se o sistema não sabe, ele mostra o traço, e não um zero. Zero é informação, ausência é outra coisa.

### A lista vazia

Uma lista vazia com a frase explicando o motivo significa que a consulta funcionou e não há nada ali. Em impedimentos, você lê "Nenhum impedimento registrado para este voo." Em MEL, "Nenhum item MEL diferido para esta aeronave." No painel, "A consulta respondeu e não há alerta aberto."

A frase existe justamente para a lista vazia não parecer defeito. Ela é o sistema afirmando o resultado, e não deixando o espaço em branco.

### A fonte fora do ar

Quando o serviço não responde, a tela diz isso com todas as letras, mostra o horário da última captura bem sucedida e informa o caminho para resolver. Ela não mostra "sem alertas" nesse caso, porque isso mentiria na direção mais perigosa possível.

Se você ver aviso de indisponibilidade, o número que está na tela é antigo, e a hora ao lado dele diz de quando. Se você ver uma lista vazia com a frase de resultado, o número é de agora.`,

  'voe-pp-05': `## Trocar o idioma e o tema da tela

Os dois controles ficam no mesmo lugar, no alto da tela e à direita, e ficam juntos. Isso vale em qualquer largura, inclusive no celular.

### Idioma

São dois botões, português e inglês, e não uma lista suspensa. Toque no que você quer e a tela inteira troca, e não apenas o cabeçalho e o menu.

Algumas coisas não são traduzidas, e isso é decisão registrada. Documento de fonte oficial mantém o texto original, e o mesmo vale para nome de norma, de artigo e para o que a própria operação escreveu. Traduzir esses produziria um texto que não existe em lugar nenhum, apresentado como se fosse o original.

### Tema

O tema claro e o escuro ficam ao lado do idioma. A escolha vale para a tela inteira, e ela fica guardada para a próxima vez que você abrir o sistema neste mesmo aparelho.

### Datas

O formato de data no despacho é sempre o brasileiro, dia, mês e ano, mesmo com a tela em inglês. Isso é proposital: prazo de documento aeronáutico brasileiro é conferido contra documento brasileiro, e alternar o formato no meio de uma conferência troca 03/09 por 09/03 sem ninguém perceber.`,

  // -- Voar: o despacho ---------------------------------------------------------------
  'voe-vr-01': `## Despachar um voo: o caminho completo na tela Voar

Voar é a tela do momento em que você está no pátio, com o voo escolhido, e precisa saber o que a operação tem registrado sobre ele. É diferente de Voos, que é onde se cadastra e se consulta.

### O caminho

1. Abra **Voar** no menu. No celular ela é o primeiro atalho da barra de baixo.
2. No campo **Voo**, escolha o voo na lista. Enquanto você não escolher, o resto da tela não aparece, e isso é proposital: uma tela com condições e impedimentos em branco pareceria carregada, e alguém leria "nenhum impedimento" como boa notícia sobre um voo que nem foi selecionado.
3. Confira o bloco **Condições do voo**: matrícula, origem, destino, regra de voo, regime e comandante. Traço em qualquer um deles significa que aquele dado não está cadastrado.
4. Leia a tabela **Impedimentos**, linha por linha. Cada linha diz a gravidade, o que impede, onde resolver e até quando valia.
5. Leia a tabela **MEL**, que mostra o equipamento inoperante com diferimento em vigor e a restrição de cada um.
6. Percorra o **Checklist** e marque o que foi conferido. Item com asterisco é obrigatório.
7. Se não houver motivo pendente, o botão **Registrar decolagem** fica disponível.

### Se não houver voo na lista

Você lê "Nenhum voo planejado" e a frase explicando: não há voo em planejamento para despachar. O caminho é cadastrar o voo em Voos, com matrícula e comandante escalado. Não é defeito da tela, é ausência de voo planejado.

### O que a tela não faz

Ela não diz que o voo pode sair. Ela mostra o que está registrado e recusa lançar um registro irregular. A decisão de operar é do comandante.`,

  'voe-vr-02': `## Impede ou Atenção: como ler a lista de impedimentos

A tabela de impedimentos traz uma linha por impedimento, e nunca uma resposta de sim ou não. "Não pode decolar" sem o motivo faz você caçar o que está vencido documento por documento, e é justamente esse tempo que a tabela existe para poupar.

### As quatro colunas

**Gravidade** traz uma de duas palavras. **Impede** é o que bloqueia o registro da decolagem agora. **Atenção** é o que precisa ser resolvido e não impede o voo de hoje, como um documento que vence daqui a cinco dias. A informação está na palavra, e não na cor, para quem não distingue as cores ler a mesma coisa.

**O que impede** é a frase que descreve o problema sem você precisar abrir outra tela.

**Onde resolver** diz de onde o impedimento veio: a aeronave, o tripulante, a norma. É a coluna que aponta em qual cadastro está a correção.

**Venceu em** traz a data até quando o item valia. Quando aparece um traço, aquele impedimento não é de prazo, e sim de estado. Aeronave em manutenção é um exemplo: não há data ali, e inventar uma seria preencher ausência com palpite.

### As duas famílias

Os impedimentos vêm de duas origens: a aeronave e a tripulação. Documento de aeronave sem data cadastrada não conta como vencido, e isso é decisão registrada: falta de cadastro é lacuna, e tratar lacuna como vencimento faria a tela acusar aeronave regular.

### O limite honesto desta lista

Nem tudo é verificável automaticamente. A inspeção por horas fica de fora porque depende do total de horas da célula naquele instante, e o item de MEL vencido também não entra nesta apuração. Lista vazia significa que nada foi encontrado pelos critérios que o sistema consegue verificar, e não que o voo está liberado.`,

  'voe-vr-03': `## O botão de registrar decolagem está apagado: o que fazer

Quando o botão está indisponível, a tela mostra logo acima dele um bloco começando com "Este voo ainda não pode decolar:" e a lista dos motivos, um por linha. O motivo vem antes do botão de propósito: botão apagado sem explicação ao lado manda você procurar o que falta em quatro telas.

### Os motivos e onde resolver cada um

**"Nenhum comandante escalado neste voo".** Abra Voos, encontre o voo, e use a escala de tripulação no detalhe dele. Escolha o tripulante, escolha a função comandante, e confirme em Escalar. Este é o motivo mais comum, e é também o que o registro recusa mesmo que você tente por outro caminho.

**"O voo não tem aeronave definida".** O voo foi cadastrado sem matrícula, ou a aeronave não está na frota. Abra Voos e complete o cadastro do voo. Se a aeronave ainda não existe, cadastre a frota primeiro.

**Qualquer linha vinda da tabela de impedimentos com gravidade Impede.** O texto do motivo é o mesmo da linha, e a coluna "Onde resolver" daquela linha aponta o cadastro. Documento vencido de aeronave se resolve em Frota; habilitação e licença, em Tripulação.

**"Item de checklist pendente:" seguido da descrição.** Volte ao checklist na mesma tela e marque o item, se ele de fato foi conferido. Não marque o que não foi conferido: a marcação é registro do que aconteceu, e não formalidade para destravar o botão.

### Se o erro aparecer depois do clique

Pode acontecer de o registro ser recusado mesmo com o botão disponível, e a mensagem aparece em vermelho, vinda do próprio sistema de registro. Ela é mostrada como veio, sem tradução, porque costuma dizer exatamente o que fazer, como "Escale o comandante antes de registrar a decolagem". Leia a frase e execute o que ela pede.`,

  'voe-vr-04': `## MEL: equipamento inoperante com diferimento em vigor

A tabela de MEL é o inverso da tabela de impedimentos. Ali estão os equipamentos que **estão inoperantes** e cujo diferimento está em vigor, dentro da restrição e do prazo. Ela aparece ao lado dos impedimentos de propósito: quem despacha precisa ver o que está diferido **antes** de decidir, e não descobrir depois que a aeronave voltou.

### As cinco colunas

**Item** é o código do equipamento na MEL daquela aeronave.

**Descrição** é o que ele é, em texto.

**Categoria** é A, B, C ou D, e é ela que define o prazo do diferimento.

**Restrição** é a limitação operacional que o diferimento impõe. Quando não há nenhuma, a coluna diz "Sem restrição operacional", que é diferente de ficar em branco: em branco se leria como campo que ninguém preencheu.

**Prazo até** é a data limite daquele diferimento.

### O que fazer com essa informação

A restrição é a parte que muda a sua operação do dia. Um diferimento pode limitar o tipo de operação, a rota ou as condições, e essa limitação precisa ser considerada por quem planeja e por quem comanda o voo.

O prazo é o que você acompanha no painel. Item de MEL entra na lista do que vence, então ele aparece com antecedência, e não no dia em que expira.

### Se a tabela estiver vazia

Você lê "Nenhum item MEL diferido para esta aeronave." Isso quer dizer que a consulta funcionou e não há diferimento registrado para aquela aeronave. Como toda ausência neste sistema, é o resultado afirmado, e não espaço em branco.`,

  'voe-vr-05': `## Checklist do voo: item obrigatório e item comum

O checklist da tela de despacho lista o que precisa ser conferido naquele voo, para aquela aeronave e aquele regime de operação. Cada linha tem uma caixa de marcação, e o texto inteiro da linha é área de toque, para funcionar no dedo e não só no cursor.

### O asterisco

Item com asterisco vermelho ao lado é obrigatório. Enquanto ele não estiver marcado, ele aparece na lista de motivos que impedem o registro da decolagem, com o texto "Item de checklist pendente:" seguido da descrição.

Item sem asterisco também deve ser conferido, e a falta dele não impede o registro.

Para quem usa leitor de tela, a palavra "obrigatório" é falada por extenso, porque a palavra asterisco não significa nada quando ouvida.

### O que a marcação significa

A marcação é o registro de que aquele item foi conferido, e nada mais. O sistema não marca nada por conta própria e não presume conferência. Item não conferido aparece como não conferido, mesmo que tudo o mais esteja em ordem.

Marcar um item que não foi conferido, para destravar o botão, transforma o registro num documento que descreve algo que não aconteceu. O checklist existe exatamente para o que não pode ser esquecido.

### Se não houver checklist

A tela mostra "Nenhum item de checklist" e explica: não há checklist configurado para aquela aeronave ou para aquele regime de operação. Isso não significa que nada precisa ser conferido no voo, significa que o sistema não tem lista configurada para aquele caso. Quem responde pela operação decide se configura uma.`,

  'voe-vr-06': `## Registrar a decolagem: a confirmação e o que não tem desfazer

Registrar a decolagem é o que tira o voo do planejamento e o transforma em registro. É a última ação da tela de despacho, e é a única que pede confirmação.

### O passo a passo

1. Com todos os motivos resolvidos, clique em **Registrar decolagem**.
2. A tela substitui o botão por uma frase e dois botões. A frase é: "Registrar a decolagem lança o voo no diário de bordo, que é assinado e imutável. Confirma?"
3. Clique em **Confirmar decolagem** para gravar, ou em **Cancelar** para voltar sem gravar.
4. Enquanto grava, o botão mostra "Registrando..." e para de aceitar clique, para evitar registro duplicado.

### Por que existe a confirmação

O diário de bordo é assinado e imutável. Não há como apagar um lançamento nem corrigir com um clique. A confirmação é o único atrito legítimo desta tela, e a frase dela diz o que é irreversível, e não apenas "tem certeza". Quem lê "tem certeza" clica em sim sem saber o que está confirmando.

### Se o registro for recusado

A mensagem aparece em vermelho, com o texto que o sistema de registro devolveu. Ela costuma dizer o que fazer. O registro tem uma verificação própria, independente do que a tela já mostrou, e ela vale para qualquer caminho de gravação: mesmo que a tela tenha deixado o botão disponível, a decolagem sem comandante escalado é recusada na gravação.

Isso não é redundância inútil. A tela avisa antes para você resolver com tempo, e a verificação da gravação garante que nenhum registro irregular entre no diário, venha de onde vier.`,

  // -- Painel e indicadores -----------------------------------------------------------
  'voe-pn-01': `## O que o painel mostra

O painel responde uma pergunta: como está a operação agora. Ele reúne, numa tela, o que exige olhar hoje e o que vai exigir olhar nos próximos dias.

### Os quatro números do topo

**Voos hoje** é a contagem de voos planejados para o dia. Se aparecer um traço no lugar do número, o sistema não conseguiu apurar, e o motivo vem escrito ao lado. Traço e zero são coisas diferentes: zero quer dizer que não há voo hoje, e o traço quer dizer que não foi possível saber.

**Alertas abertos** é o total do que está aberto. Quando há filtro aplicado, o cartão mostra também quantos sobraram depois do filtro.

**Críticos** é o recorte dos alertas de maior severidade.

**Vencidos** é a contagem do que já passou do prazo.

### Os dois gráficos

**Alertas por severidade** separa em crítico, atenção e informativo. **Alertas por área** separa pela origem do alerta: frota, tripulação, MEL, conformidade. Os dois são clicáveis, e filtram a tela.

### As duas listas

**Alertas abertos** traz cada alerta em texto, e é onde você vê o que os gráficos apontaram. **O que vence** traz habilitação, licença, inspeção e item de MEL com data, separados por faixa de prazo.

### O que o painel não faz

Ele aponta, e não decide. Nenhum número aqui autoriza operação, e a leitura de um painel limpo não substitui a conferência do despacho na tela Voar.`,

  'voe-pn-02': `## Filtrar clicando no gráfico, e os recortes que somam

Nenhum gráfico deste painel é figura. O número que aponta um problema precisa levar até ele, e por isso cada barra é clicável.

### Como filtrar

Clique numa barra para filtrar a tela por aquele recorte. Clique de novo na mesma barra para desfazer. A legenda também é clicável, e liga ou desliga a série.

Os cartões de contagem no topo funcionam do mesmo jeito: clicar em Críticos filtra a tela pelos críticos.

### Os recortes somam, e não se substituem

Esta é a parte que mais surpreende quem vem de outros painéis. Clicar em "Vencido" e depois em "Hoje" mostra **os dois**, e não troca um pelo outro. O mesmo vale para as áreas: clicar em frota e depois em tripulação mostra as duas.

E os dois eixos se combinam. O filtro por severidade e o filtro por área são independentes, então você pode pedir os críticos **da** tripulação, que é a pergunta que um painel precisa responder para valer o espaço que ocupa.

O gráfico de áreas traz essa instrução escrita abaixo do título: clique numa barra para somar o recorte ao filtro de severidade.

### Como saber o que está filtrado

Sempre que houver filtro, aparece uma faixa dizendo "Mostrando" com o número de alertas exibidos, "de" e o total. Ao lado dela há o botão **Limpar filtros**, que desfaz tudo de uma vez.

Se a lista de alertas ficar vazia com filtro aplicado, você lê "Nenhum alerta corresponde ao filtro. Limpe o filtro para ver todos.", que é diferente de não haver alerta nenhum.`,

  'voe-pn-03': `## O que vence: vencido, hoje, próximo e futuro

A lista "O que vence" reúne o que tem prazo: habilitação e licença de tripulante, inspeção de aeronave e item de MEL diferido. Ela existe para o vencimento aparecer enquanto ainda dá tempo de renovar, e não no dia em que ele impede um voo.

### As quatro faixas

**Vencido** é o que já passou do prazo. Aqui não há mais antecipação possível: o item já está impedindo ou vai impedir na próxima tentativa de despacho.

**Hoje** é o que vence no dia. É a faixa que decide o seu dia, porque um documento que vence hoje ainda vale hoje.

**Próximo** é o que vence nos dias seguintes, dentro do período que a lista acompanha.

**Futuro** é o que ainda está longe, e aparece para planejamento.

A faixa é calculada a partir da data do item, e não é digitada por ninguém. Isso significa que um item muda de faixa sozinho conforme o tempo passa.

### O que fazer com cada faixa

O que está em Vencido e em Hoje se resolve hoje, no cadastro de origem: Frota para documento e inspeção de aeronave, Tripulação para habilitação e licença.

O que está em Próximo é o que se agenda. Renovar com uma semana de antecedência custa o mesmo que renovar no dia, e não para nenhum voo.

### Se a lista estiver vazia

Você lê "A consulta respondeu e nada vence no período." A frase é assim de propósito: ela afirma que a consulta funcionou, para a lista vazia não ser confundida com falha de carregamento.`,

  'voe-pn-04': `## Por que alguns números do painel não são clicáveis

No painel, alguns números filtram a tela quando você clica, e outros não reagem. Isso não é falha nem inconsistência: é uma decisão para a tela não prometer o que não cumpre.

### A regra

**Contagem é clicável.** Alertas abertos, Críticos e Vencidos são recortes de uma lista que existe na tela. Clicar neles filtra o que está sendo mostrado, porque há uma lista para recortar.

**Taxa, média e projeção não são clicáveis.** Uma taxa não é recorte de uma lista: não existe "a lista dos itens que compõem esta taxa" para mostrar. Dar aparência de botão a ela faria você clicar e nada acontecer.

### Por que isso importa

Um botão que não faz nada é pior que um número comum. Depois do primeiro clique sem resposta, você passa a desconfiar dos cliques que funcionam, e o painel inteiro perde utilidade.

Então a tela é honesta na aparência: o que parece clicável filtra, e o que não filtra não parece clicável.

### Quando um gráfico não tem dado

Ele não desaparece e não inventa uma série de exemplo. Ele mostra o eixo vazio e diz o motivo. Gráfico que some faz você achar que a tela quebrou, e gráfico com série inventada é pior, porque parece informação.`,

  'voe-pn-05': `## Painel sem alerta nenhum não é painel quebrado

Quando não há alerta aberto, o painel diz isso com uma frase, e não deixa o espaço em branco. Você lê "A consulta respondeu e não há alerta aberto." nos gráficos e na lista.

### Por que a frase é assim

Ela afirma duas coisas de uma vez: que a consulta funcionou, e que o resultado dela é zero. Uma tela em branco não distingue essas duas situações, e num sistema de aviação essa confusão é perigosa na direção mais grave possível: alguém veria "sem alertas" quando o serviço caiu, e concluiria que está tudo em ordem.

### Como saber a diferença

**Lista vazia com a frase de resultado:** a consulta rodou agora e não encontrou nada. É boa notícia, e o número é atual.

**Aviso de indisponibilidade:** o serviço não respondeu. A tela diz isso, mostra a hora da última captura bem sucedida e o caminho para resolver. Aqui o que você está vendo é antigo, e a hora ao lado diz de quando.

**Traço no lugar de um número:** aquele indicador específico não pôde ser apurado, e o motivo vem escrito. Não é zero.

### O que fazer em cada caso

Com a frase de resultado, siga o dia normalmente, e continue conferindo o despacho na tela Voar antes de cada voo. Painel limpo não substitui a conferência do voo, porque ele mostra o conjunto da operação e não as condições daquele voo específico.

Com aviso de indisponibilidade, trate os números da tela como desatualizados e avise quem cuida do ambiente.`,

  // -- Voos e escala ------------------------------------------------------------------
  'voe-vo-01': `## Cadastrar um voo

O cadastro fica em Voos. Clique em **Novo voo** e preencha o formulário. Ao final, o botão é **Cadastrar voo**, e durante a gravação ele mostra "Cadastrando...".

### Os campos

**Número do voo.** É único dentro da sua organização, e a ajuda do campo diz isso. Se você tentar repetir um número já usado, o cadastro é recusado. Use outro número, ou localize o voo que já existe.

**Aeronave.** Escolha na lista da frota. Se a lista aparecer com "Nenhuma aeronave cadastrada. Cadastre a frota antes de lançar o voo.", o caminho é cadastrar a aeronave em Frota e voltar.

**Origem e Destino.** Os aeródromos do trecho.

**Partida estimada e Chegada estimada.** A chegada precisa ser igual ou posterior à partida, e a ajuda do campo avisa isso. Se você inverter, o formulário recusa: corrija a data ou a hora antes de enviar.

**Regra de voo.** VFR, IFR, Y, Z ou VFR noturno.

**Regime.** O regulamento sob o qual a operação acontece: RBAC 91, 121, 135 ou 137.

**Passageiros.** A quantidade prevista.

**Observações.** É obrigatória, e a ajuda diz por quê: é o registro do que a operação precisa saber sobre aquele voo. Enviar em branco é recusado, e o campo marcado com asterisco avisa antes.

### Depois de cadastrar

O voo entra na lista como planejado. Ele ainda não está pronto para despacho: falta escalar a tripulação, e sem comandante escalado a decolagem não é registrada. Essa pendência aparece no próprio detalhe do voo.`,

  'voe-vo-02': `## Escalar a tripulação de um voo

A escala fica dentro do detalhe do voo, e não numa tela separada. Escalar é uma ação sobre um voo, então ela vive onde o voo está.

### Como escalar

1. Abra **Voos** e clique no voo na lista.
2. Role até a seção **Escala de tripulação**.
3. No campo **Tripulante**, escolha a pessoa.
4. No campo **Função**, escolha comandante, copiloto ou comissário.
5. Clique em **Escalar**.

A pessoa passa a aparecer na tabela de escala daquele voo. Para desfazer, use **Remover** na linha correspondente.

### Se ninguém estiver escalado

A tabela mostra "Ninguém escalado ainda. Escolha o tripulante e a função abaixo." A frase aponta para o campo logo abaixo, de propósito: dizer apenas que não há ninguém escalado, sem oferecer como escalar, faz parecer falta de cadastro da pessoa quando é só uma ação que ninguém executou ainda.

### Por que o comandante é diferente

A seção traz a frase: o voo não decola sem comandante escalado, e é o nome que a autoridade procura no diário. Registrar a decolagem sem comandante escalado é recusado pelo sistema de registro, e essa recusa vale para qualquer caminho de gravação, e não apenas para a tela de despacho.

Então escalar o comandante é o passo que transforma um voo cadastrado num voo despachável. Fazer isso na véspera custa dois minutos; descobrir a falta no pátio custa o atraso.`,

  'voe-vo-03': `## Situação e status do voo: duas perguntas diferentes

Na lista de voos existem dois conceitos que se parecem e respondem coisas distintas. Confundi-los faz alguém procurar problema onde não há.

### Status: onde o voo está no ciclo

São quatro: **planejado**, **em voo**, **concluído** e **cancelado**. O status descreve o ponto do voo na linha do tempo, e muda por eventos: registrar a decolagem leva de planejado para em voo, e assim por diante.

### Situação: se ele precisa de atenção

A situação recorta a lista por urgência, e não por etapa. Um voo **planejado sem comandante escalado é crítico**, porque falta algo para ele acontecer. Um voo **concluído é considerado em ordem**, mesmo tendo terminado há um mês, porque não há nada a resolver nele.

### Por que os dois existem

Se a lista tivesse só o status, você não conseguiria ver de relance quais voos precisam de você hoje: os planejados apareceriam todos iguais, os prontos e os incompletos misturados.

Se tivesse só a situação, você não saberia onde cada voo está no ciclo, e um voo cancelado se confundiria com um planejado sem pendências.

### Como usar isso na prática

Comece o dia pela situação, para achar o que exige ação. Use o status para saber o que já saiu, o que ainda vai sair e o que foi cancelado. Um voo com situação crítica traz o motivo no detalhe, nomeado item por item.`,

  'voe-vo-04': `## Ver o que falta para um voo decolar antes de ir ao pátio

O detalhe do voo mostra as pendências enquanto ainda dá tempo de resolver. Essa é a diferença entre descobrir na véspera, com a aeronave no hangar, e descobrir no pátio, com todo mundo esperando.

### Onde ver

Abra **Voos**, clique no voo, e role o detalhe. Quando há pendência, aparece um bloco começando com "Este voo ainda não pode decolar:" seguido da lista.

É o mesmo texto que a tela de despacho mostra, e isso é proposital: os dois lugares dizem a mesma coisa com as mesmas palavras, então você não precisa aprender dois vocabulários.

### As pendências que aparecem aqui

**"O voo não tem aeronave definida".** Complete o cadastro do voo com a matrícula. Se a aeronave não existe na frota, cadastre-a primeiro.

**"Nenhum comandante escalado neste voo".** Use a escala de tripulação, na mesma tela, logo abaixo.

### O que mais o detalhe mostra

O bloco de dados traz matrícula, origem, destino, situação, partida e chegada estimadas, regra de voo, regime e passageiros. Abaixo dele ficam as observações, e depois a escala de tripulação.

Traço em qualquer campo significa que o dado não está cadastrado, e não que a tela falhou ao carregar.

### Se o voo sumir da lista

Você lê "Voo não encontrado" e a explicação: o voo selecionado não está mais na lista, e pode ter sido removido por outra pessoa. Volte à lista e confira. Isso acontece quando alguém trabalha no mesmo cadastro ao mesmo tempo que você.`,

  'voe-vo-05': `## Voos no celular: lista e detalhe se revezam

No computador, a lista de voos e o detalhe do voo aparecem lado a lado. No celular não caberia, então eles se revezam: aparece uma coisa por vez.

### Como funciona

Você abre Voos e vê a lista. Ao tocar num voo, a lista sai e o detalhe entra ocupando a tela inteira. Para voltar, use o botão de voltar que aparece no alto do detalhe.

Esse botão só existe no celular. No computador ele não faz sentido, porque a lista continua visível ao lado.

### Nenhum voo abre sozinho

Ao entrar na tela, nenhum registro vem aberto, nem no celular nem no computador. Isso é decisão, e não esquecimento: um voo aberto sem você ter pedido sugere que ele foi escolhido por algum critério, e não foi. Você escolhe qual voo quer ver.

### Tudo o que existe no computador existe no telefone

Cadastrar voo, escalar tripulação, remover alguém da escala e ler as pendências funcionam igual nos dois. Não há recurso que exista só na tela grande.

Voos é também um dos quatro atalhos fixos da barra inferior do celular, junto com Voar, Painel e Tripulação, então você chega nele com um toque, de qualquer lugar do sistema.

### Marcar checklist no telefone

Na tela de despacho, a linha inteira do item de checklist é área de toque, e não apenas a caixinha. Isso existe porque uma caixa pequena erra no dedo, e errar a marcação de um checklist de voo não é um erro qualquer.`,
};

/* ===== bloco 2-frota ===== */
const ARTIGOS_2: ArtigoDaBase[] = [
  // ==========================================================================
  // FROTA E AERONAVES
  // ==========================================================================
  {
    id: 'voe-fr-01',
    titulo: 'Como cadastrar uma aeronave',
    resumo: 'O passo a passo do cadastro, da matrícula às horas de célula.',
    marcadores: ['frota', 'cadastro', 'matrícula'],
    categoriaId: 'frota',
  },
  {
    id: 'voe-fr-02',
    titulo: 'Por que a Frota não diz se a aeronave pode voar',
    resumo: 'A tela mostra os prazos e quem conclui é uma pessoa. Entenda o motivo.',
    marcadores: ['frota', 'aeronavegabilidade', 'decisão'],
    categoriaId: 'frota',
  },
  {
    id: 'voe-fr-03',
    titulo: 'CVA, CA e seguro: as três validades da aeronave',
    resumo: 'São três documentos distintos, que vencem em datas distintas.',
    marcadores: ['frota', 'validade', 'documentos'],
    categoriaId: 'frota',
  },
  {
    id: 'voe-fr-04',
    titulo: 'A inspeção vence pelo que vier primeiro: horas ou data',
    resumo: 'São dois limites independentes, e a tela mostra os dois lado a lado.',
    marcadores: ['frota', 'inspeção', 'manutenção'],
    categoriaId: 'frota',
  },
  {
    id: 'voe-fr-05',
    titulo: 'Campo em branco não quer dizer que está em dia',
    resumo: 'Validade não lançada é ausência de informação, e a tela diz isso com todas as letras.',
    marcadores: ['frota', 'validade', 'cadastro'],
    categoriaId: 'frota',
  },
  {
    id: 'voe-fr-06',
    titulo: 'Achar uma aeronave: busca, filtros e os três status',
    resumo: 'Como recortar a lista por situação e encontrar o modelo digitando duas palavras.',
    marcadores: ['frota', 'busca', 'status'],
    categoriaId: 'frota',
  },

  // ==========================================================================
  // TRIPULACAO E LICENCAS
  // ==========================================================================
  {
    id: 'voe-tr-01',
    titulo: 'Como cadastrar um tripulante',
    resumo: 'O passo a passo do cadastro, do nome ao CANAC e à validade do CMA.',
    marcadores: ['tripulação', 'cadastro', 'canac'],
    categoriaId: 'tripulacao',
  },
  {
    id: 'voe-tr-02',
    titulo: 'CMA vencido tira a pessoa de escala no mesmo instante',
    resumo: 'Não há tolerância nem prorrogação automática: acontece pelo calendário.',
    marcadores: ['tripulação', 'cma', 'escala'],
    categoriaId: 'tripulacao',
  },
  {
    id: 'voe-tr-03',
    titulo: 'Os três estados do CMA, e o do meio é o que se esquece',
    resumo: 'Válido, vencido e não lançado. O terceiro não é uma variação dos outros dois.',
    marcadores: ['tripulação', 'cma', 'cadastro'],
    categoriaId: 'tripulacao',
  },
  {
    id: 'voe-tr-04',
    titulo: 'Ativo não é apto: são dois fatos diferentes',
    resumo: 'Pertencer à operação e poder exercer a função são coisas separadas na tela.',
    marcadores: ['tripulação', 'escala', 'cadastro'],
    categoriaId: 'tripulacao',
  },
  {
    id: 'voe-tr-05',
    titulo: 'As licenças do tripulante, e o que uma lista vazia significa',
    resumo: 'A tela distingue não consultado, sem licença cadastrada e serviço indisponível.',
    marcadores: ['tripulação', 'licenças', 'habilitação'],
    categoriaId: 'tripulacao',
  },
  {
    id: 'voe-tr-06',
    titulo: 'Quando o CANAC é recusado no cadastro',
    resumo: 'O que o campo aceita, o que ele corrige sozinho, e como resolver a recusa.',
    marcadores: ['tripulação', 'canac', 'erro'],
    categoriaId: 'tripulacao',
  },

  // ==========================================================================
  // DIARIO DE BORDO
  // ==========================================================================
  {
    id: 'voe-di-01',
    titulo: 'Diário assinado não se edita: ele se retifica',
    resumo: 'Por que a ação de editar não aparece em lançamento assinado.',
    marcadores: ['diário', 'assinatura', 'retificação'],
    categoriaId: 'diario',
  },
  {
    id: 'voe-di-02',
    titulo: 'Como registrar uma retificação',
    resumo: 'O passo a passo da correção, e por que o motivo é obrigatório.',
    marcadores: ['diário', 'retificação', 'correção'],
    categoriaId: 'diario',
  },
  {
    id: 'voe-di-03',
    titulo: 'Por que a retificação foi recusada',
    resumo: 'As seis recusas possíveis, cada uma com o que fazer para resolver.',
    marcadores: ['diário', 'retificação', 'erro'],
    categoriaId: 'diario',
  },
  {
    id: 'voe-di-04',
    titulo: 'O original e a retificação ficam os dois na tela',
    resumo: 'Nada é escondido: a autoridade precisa ver o que foi lançado e o que foi corrigido.',
    marcadores: ['diário', 'retificação', 'auditoria'],
    categoriaId: 'diario',
  },
  {
    id: 'voe-di-05',
    titulo: 'Ler o extrato do diário: colunas e filtros',
    resumo: 'O que cada coluna mostra e como recortar por período e por matrícula.',
    marcadores: ['diário', 'extrato', 'filtro'],
    categoriaId: 'diario',
  },
  {
    id: 'voe-di-06',
    titulo: 'Combustível em branco não é zero, e de onde vêm as horas da aeronave',
    resumo: 'Duas confusões comuns no lançamento, e o efeito de cada uma no cadastro da frota.',
    marcadores: ['diário', 'combustível', 'horas'],
    categoriaId: 'diario',
  },
];

/**
 * O corpo de cada artigo, em markdown.
 *
 * A CHAVE E O MESMO `id` DA LISTA ACIMA, e essa e a unica ligacao entre as duas estruturas:
 * artigo sem corpo abre em branco, e corpo sem artigo nunca e alcancado. Ao somar um artigo,
 * as duas pontas mudam no mesmo momento.
 */
const CORPOS_2: Record<string, string> = {
  // ==========================================================================
  // FROTA E AERONAVES
  // ==========================================================================
  'voe-fr-01': `## Como cadastrar uma aeronave

O cadastro é o ponto de partida de quase tudo no VOE: sem a aeronave registrada, ela não aparece para ser escalada num voo nem para receber lançamento de diário.

1. Abra **Frota** no menu. A lista das aeronaves da operação aparece de um lado, e o detalhe da que você selecionar aparece do outro.
2. Clique em **Cadastrar aeronave**. Abre o formulário, dividido em quatro blocos: Identificação, Horas e ciclos, Próxima inspeção e Validades.
3. Preencha **Matrícula**. O campo espera o formato PR-ABC, que é como a matrícula aparece pintada na fuselagem. Você pode digitar em minúsculas e pode esquecer o hífen: o campo corrige os dois sozinho. O que ele não corrige é matrícula fora do padrão brasileiro, e nesse caso ele recusa. Se acontecer, confira a matrícula na aeronave antes de tentar de novo.
4. Preencha **Modelo**. É por ele que a busca da lista encontra a aeronave depois. **Fabricante** e **Ano** são opcionais.
5. No bloco Horas e ciclos, informe **Horas de célula**, **Horas de motor** e **Ciclos**. Horas de célula contam desde a fabricação e não zeram nunca. Horas de motor zeram na troca do motor, então é normal que sejam menores que as de célula. Ciclos são pousos completos.
6. Escolha a **Situação**: Ativa, Em manutenção ou Inativa. Marque **Homologada IFR** se a aeronave for homologada para voo por instrumentos.
7. Preencha o bloco Próxima inspeção e o bloco Validades. Os dois têm artigo próprio nesta base, porque cada um tem uma armadilha conhecida.
8. Salve. A aeronave passa a aparecer na lista, e o detalhe dela mostra os prazos com a distância para o vencimento.

O número de horas de célula que você digita aqui é o ponto de partida. Depois do primeiro lançamento no diário de bordo, ele passa a crescer sozinho com o que o diário registra, e é por isso que você não precisa voltar aqui para atualizá-lo a cada voo.`,

  'voe-fr-02': `## Por que a Frota não diz se a aeronave pode voar

A tela de Frota mostra os fatos que estão no cadastro e não escreve em lugar nenhum que a aeronave está apta ou impedida. Isso é decisão de projeto, e não algo que faltou construir.

O motivo é simples de enunciar: **aeronavegabilidade depende de coisas que este cadastro não conhece**. A inspeção que acabou de ser feita e ainda não foi lançada. O item inoperante aberto na lista de equipamento mínimo. A exigência do regime de operação em que aquele voo vai acontecer. E, acima de tudo, a decisão do responsável técnico.

Uma tela que anunciasse "apta" com base em três datas estaria afirmando o que ninguém mediu. O custo desse erro não é uma linha errada num relatório: é alguém decolar confiando nela.

O que a tela faz, e faz por inteiro:

- mostra cada validade com a distância para o vencimento, em dias;
- mostra quanto falta para a próxima inspeção, nos dois limites que existem;
- deixa evidente o que já venceu, em vez de misturar com o que está em dia;
- diz com todas as letras o que não foi lançado, em vez de deixar o campo vazio parecer normal.

Quem conclui é você. A tela existe para que essa conclusão seja tomada com os prazos à vista, e não de memória.

O mesmo raciocínio vale em sentido contrário: se a tela mostra uma validade vencida, isso não é opinião do sistema. É a data que está no cadastro comparada com hoje. Se o documento foi renovado e a tela ainda mostra vencido, o que falta é lançar a validade nova, e não discutir com a tela.`,

  'voe-fr-03': `## CVA, CA e seguro: as três validades da aeronave

O cadastro guarda três documentos com data de validade, e eles são coisas diferentes que vencem em datas diferentes:

- **Validade do CVA**, o Certificado de Verificação de Aeronavegabilidade;
- **Validade do CA**, o Certificado de Aeronavegabilidade;
- **Validade do seguro**, a apólice.

No detalhe da aeronave, no bloco **Validades**, os três aparecem sempre, um em cada linha, com a data e a distância para o vencimento. Eles aparecem mesmo quando não têm data lançada, e nesse caso a linha diz **Sem data lançada**. Nada é escondido: uma lista que mostrasse só o que está vencendo daria lista vazia para a aeronave sem cadastro nenhum, e lista vazia é lida como tudo certo.

**O dia da validade ainda vale.** Um certificado com validade em 06 de setembro é válido no dia 06 e passa a vencido no dia 07. É a leitura de "válido até", e a tela segue exatamente essa contagem. Se você esperava ver vencido no próprio dia, o que a tela mostra está certo.

Para lançar ou corrigir uma validade, abra a aeronave, clique em **Editar aeronave**, preencha o campo correspondente no bloco Validades e salve. A linha do detalhe muda na hora.

Um cuidado que evita retrabalho: renove o cadastro no mesmo momento em que o documento novo chega às suas mãos. Enquanto a data antiga estiver lá, a tela vai continuar mostrando vencido, e quem olhar a frota vai tomar decisão com base no que está escrito, que é o comportamento correto dela.`,

  'voe-fr-04': `## A inspeção vence pelo que vier primeiro: horas ou data

A próxima inspeção tem **dois limites independentes**, e o cadastro guarda os dois:

- uma **marca de horas de célula**, o horímetro em que a inspeção cai;
- uma **data de calendário**.

A inspeção vence no que chegar primeiro. Uma aeronave parada vence pela data enquanto uma aeronave intensa vence pelas horas, e por isso a tela mostra os dois lado a lado e nunca escolhe um para exibir. Escolher esconderia justamente o limite que está prestes a cair.

No detalhe, o bloco **Próxima inspeção** traz as duas linhas:

- **Inspeção na marca de** mostra a marca de horas e quantas horas de célula faltam para chegar nela. Quando a marca já passou, a tela escreve que a marca de horas da inspeção já passou, e por quantas horas.
- **Inspeção até** mostra a data e quantos dias faltam.

Quando nenhum dos dois foi lançado, a tela diz que nenhum limite de inspeção foi lançado, nem por horas nem por data. Isso não é a mesma coisa que inspeção longe: é cadastro vazio, e a saída é preencher.

Para lançar, clique em **Editar aeronave** e preencha **Marca** com a marca de horas de célula em que a inspeção vence, e **Data** com a data limite. Você pode preencher só um dos dois se a operação só controla um deles, e a tela vai dizer que o outro não foi lançado, em vez de fingir que ele não existe.

Um detalhe que já confundiu gente: o campo **Marca** é o valor absoluto do horímetro, e não quantas horas faltam. Se a aeronave tem 1.200 horas de célula e a inspeção é daqui a 100 horas, o valor a preencher é 1.300, e não 100. A tela é que faz a conta e mostra quanto falta.`,

  'voe-fr-05': `## Campo em branco não quer dizer que está em dia

Esta é a confusão mais cara do cadastro de frota, e a tela foi desenhada para não permiti-la.

Quando uma validade não tem data lançada, isso significa que **aquele documento não foi registrado**, e não que ele está válido. As duas frases são opostas, e tratar uma como a outra leva a decisões contrárias: uma manda procurar o documento, a outra manda seguir tranquilo.

Por isso a tela separa os dois casos em toda parte:

- o que tem data entra na lista de vencimentos, com os dias que faltam ou os dias que já passaram;
- o que não tem data aparece nomeado, numa linha própria, dizendo **Sem data lançada**;
- quando nenhuma das três validades foi lançada, o bloco escreve que nenhuma validade foi lançada para esta aeronave, em vez de aparecer vazio.

O mesmo vale para a inspeção: **Sem marca de horas lançada para a próxima inspeção** e **Data não lançada** são frases que a tela escreve de propósito, no lugar onde um campo vazio passaria despercebido.

Se você encontrar uma dessas frases, o caminho é sempre o mesmo: abra a aeronave, clique em **Editar aeronave**, preencha o campo que está faltando e salve. Se o dado não está com você, quem sabe é a manutenção ou o responsável técnico, e é melhor a linha continuar dizendo que falta a lançar do que alguém preenchê-la com um palpite.

Uma última observação, e ela vale para toda a plataforma: número em tela ou é real ou é um traço. Você não vai encontrar aqui uma estimativa apresentada como se fosse fato.`,

  'voe-fr-06': `## Achar uma aeronave: busca, filtros e os três status

A lista de Frota tem duas formas de recorte, e elas funcionam juntas.

**A busca por modelo aceita palavras soltas.** Digitar "king 350" encontra o King Air 350i, mesmo você não escrevendo o nome completo com a pontuação certa. A busca procura cada palavra que você digitou, em qualquer ordem, e não um trecho contínuo. Ela também ignora acento e maiúsculas, então não é preciso caprichar na digitação. O que ela não faz é devolver a frota inteira com uma palavra genérica: se você digita duas palavras, as duas precisam aparecer no modelo.

**Os filtros recortam por situação**, e são quatro:

- **Todas**, sem recorte;
- **Vencida ou parada**, o que exige ação agora;
- **Vence em breve**, o que exige agendamento;
- **Em dia**, o resto.

Repare que o filtro fala de prazos e de parada, e não de aptidão. A tela não classifica aeronave como apta, pelo motivo explicado no artigo sobre por que a Frota não decide se a aeronave pode voar.

**Os três status do cadastro** significam coisas diferentes, e é você quem os escolhe no formulário:

- **Ativa**: em operação normal.
- **Em manutenção**: parada em manutenção, e volta quando a manutenção liberar.
- **Inativa**: fora de operação por decisão do operador, sem previsão de volta.

A diferença entre os dois últimos é a que mais se erra. Manutenção descreve uma parada com volta prevista; inativa descreve uma decisão do operador sem previsão. Marcar uma pela outra muda o que a próxima pessoa entende ao abrir a lista, e ninguém tem como adivinhar a intenção depois.`,

  // ==========================================================================
  // TRIPULACAO E LICENCAS
  // ==========================================================================
  'voe-tr-01': `## Como cadastrar um tripulante

1. Abra **Tripulação** no menu. A lista dos tripulantes da operação aparece de um lado, e o detalhe de quem você selecionar aparece do outro.
2. Clique em **Cadastrar tripulante**. Abre o formulário, com os blocos Identificação e Validade do CMA.
3. Preencha **Nome**.
4. Preencha **CANAC**, que é o código ANAC do tripulante. O campo é opcional no cadastro, e aceita de cinco a sete dígitos. Ponto e espaço podem ser digitados: o campo os remove sozinho. Se ele recusar o que você digitou, veja o artigo sobre o CANAC recusado nesta mesma categoria.
5. Preencha **Classe do CMA** com a classe do certificado médico. É um rótulo, e não uma quantidade.
6. Preencha **Validade do CMA**. Este é o campo mais importante da tela, e o artigo sobre CMA vencido explica por quê. Se você não tiver a data agora, deixe em branco: a tela vai dizer que não foi lançado, o que é verdade, em vez de assumir que está válido.
7. Preencha **Horas totais** se quiser. É opcional, e começa em zero se não informado.
8. Marque ou desmarque **Ativo na operação**. Ativo significa que a pessoa pertence à operação, e não que ela pode voar hoje.
9. Salve. O tripulante passa a aparecer na lista.

Para alterar depois, abra o tripulante e clique em **Editar tripulante**.

As licenças e habilitações não são cadastradas neste formulário: elas vêm em lista própria, dentro do detalhe do tripulante, e têm artigo separado nesta base.

Uma observação sobre dado pessoal: o CANAC e o número da licença são guardados cifrados, e aparecem apenas para quem abre o cadastro. Eles não são usados como identificador em nenhuma outra parte da tela, e não aparecem na lista, que fica visível o tempo todo sem ninguém ter pedido para ver.`,

  'voe-tr-02': `## CMA vencido tira a pessoa de escala no mesmo instante

O Certificado Médico Aeronáutico **não tem tolerância e não tem prorrogação automática**. No dia seguinte ao vencimento, a pessoa deixa de poder exercer a função. Isso não depende de ninguém lançar nada no sistema: acontece pelo calendário.

Esse fato muda o desenho da tela inteira. Uma validade de CMA vencida não é um prazo em atraso como os outros, que se resolve na próxima semana: é uma pessoa que não pode ser escalada agora. Por isso o vencimento do CMA **não fica só na lista de prazos junto dos demais**, onde seria mais um item numa lista. Ele ganha um bloco próprio, no topo do detalhe, escrito com todas as letras: **Fora de escala: CMA vencido**, seguido da observação de que o CMA não tem tolerância e a pessoa não pode exercer a função até renovar.

Na lista, quem está nessa condição aparece com a marca de fora de escala, e o filtro **Fora de escala** recorta exatamente esse grupo.

**O dia da validade ainda vale.** Um CMA com validade em 06 de setembro vale no dia 06 e vence no dia 07. Na véspera, a tela avisa que, vencido, o tripulante sai de escala no dia seguinte. É o momento de agir, e não o momento de discutir a contagem.

O que fazer quando aparecer:

1. Trate a escala primeiro. Enquanto o certificado não for renovado, essa pessoa não entra em voo, e é melhor descobrir isso agora do que no pátio.
2. Quando o certificado novo estiver em mãos, abra o tripulante, clique em **Editar tripulante**, atualize **Validade do CMA** e salve. O bloco de aviso sai do topo na hora.

O que **não** resolve: apagar a data para o aviso sumir. Sem validade lançada, a tela passa a dizer que não é possível afirmar que a pessoa pode ser escalada, que é uma frase diferente e igualmente impeditiva, e você perde a informação de quando o certificado venceu.`,

  'voe-tr-03': `## Os três estados do CMA, e o do meio é o que se esquece

O certificado médico aparece na tela em um de três estados, e eles não são variações um do outro:

- **CMA válido**: a data existe e não passou. A tela mostra até quando.
- **Fora de escala: CMA vencido**: a data passou. A pessoa não pode exercer a função.
- **CMA não lançado**: não há data nenhuma no cadastro. **Não sabemos** se a pessoa pode voar.

O terceiro é o que se esquece, e é justamente o que a tela mais protege. Tratar "sem data" como vencido acusaria de impedida uma pessoa que talvez esteja em dia, e obrigaria alguém a correr atrás de um problema que pode não existir. Tratar como válido é pior: liberaria para escala alguém cujo documento ninguém conferiu.

A tela então diz que não sabe, que é a única das três frases que é verdade. No detalhe, o texto é direto: sem a validade do CMA não é possível afirmar que esta pessoa pode ser escalada.

Na lista, o filtro **Vence em breve ou sem CMA** junta dois grupos de propósito: quem está prestes a vencer e quem não tem data lançada. São motivos diferentes para olhar a mesma pessoa, e os dois pedem uma providência antes da próxima escala.

O que fazer em cada caso:

- **Não lançado**: procure o certificado, abra o tripulante, clique em **Editar tripulante** e preencha **Validade do CMA**.
- **Vencido**: providencie a renovação e atualize a data quando o documento novo chegar.
- **Válido**: nada a fazer, e vale conferir a distância para o vencimento que a tela mostra ao lado.`,

  'voe-tr-04': `## Ativo não é apto: são dois fatos diferentes

O cadastro tem uma marca chamada **Ativo na operação**, e ela responde uma pergunta só: essa pessoa **pertence** à operação? Quem saiu da empresa, quem está afastado por decisão administrativa ou quem nunca chegou a começar fica como **Inativo**.

O CMA responde outra pergunta: essa pessoa **pode exercer a função**?

As duas perguntas são independentes, e as quatro combinações existem de verdade:

- ativo e com CMA válido: pertence e pode exercer;
- ativo e com CMA vencido: pertence, e não pode ser escalada;
- ativo e sem CMA lançado: pertence, e não sabemos;
- inativo: não pertence, e o resto é consequência disso.

A tela mostra os dois fatos separados, e não os funde num único rótulo de disponível. Fundir apagaria a diferença justamente no caso em que ela importa, que é o segundo da lista: uma pessoa que está no quadro, aparece na escala, e não pode voar.

Na prática isso significa que **desmarcar Ativo não é o jeito de tratar um CMA vencido**, e nem o contrário. Se a pessoa continua na operação e só o certificado venceu, ela continua ativa, e o que muda é a validade do CMA. Se a pessoa saiu, aí sim desmarque **Ativo na operação**, com o certificado como estiver.

Para alterar, abra o tripulante, clique em **Editar tripulante**, ajuste a marca e salve. A lista passa a mostrar **Inativo** ao lado do nome, e o filtro de situação a considera a partir dali.`,

  'voe-tr-05': `## As licenças do tripulante, e o que uma lista vazia significa

As licenças e habilitações aparecem no bloco **Licenças**, dentro do detalhe de cada tripulante. Elas vêm de uma consulta separada da consulta do cadastro, e por isso o bloco pode estar em situações diferentes, cada uma com uma frase própria:

- **Licenças não consultadas**: você ainda não abriu ninguém. A tela pede para abrir o tripulante para carregar as licenças dele.
- **Carregando as licenças**: o pedido saiu e ainda não voltou. Espere a lista aparecer antes de concluir qualquer coisa.
- A lista com os itens: cada linha traz o tipo, o número e a validade.
- **Nenhuma licença cadastrada**: o serviço respondeu, e não há licença registrada para este tripulante. Isso é um fato medido, e não uma falha.
- **Não foi possível carregar as licenças**: o serviço não respondeu. A tela mostra a mensagem que veio do servidor, que é o que se repete ao suporte.

A distinção entre as duas últimas é o ponto do bloco inteiro. Lista vazia por falha e lista vazia por ausência são a mesma tela em branco e significam coisas opostas: uma manda tentar de novo mais tarde, a outra manda cadastrar a licença.

Sobre as validades: cada licença tem a sua, e uma habilitação vencida restringe **o que** a pessoa pode operar, enquanto o CMA vencido alcança a pessoa inteira. Quando nenhuma das licenças cadastradas tem validade lançada, a tela escreve exatamente isso, em vez de deixar a coluna vazia.

O tipo da licença é texto livre, então ele aparece na tela como foi cadastrado. Vale combinar dentro da operação uma forma única de escrever cada tipo, porque a tela mostra o que recebeu e não corrige grafia.`,

  'voe-tr-06': `## Quando o CANAC é recusado no cadastro

O campo **CANAC** aceita **de cinco a sete dígitos, e nada além de dígito**. Essa faixa não é escolha da tela: é o que o sistema valida no momento de gravar, e a tela apenas avisa antes para você não preencher o formulário inteiro e receber o erro no fim.

O campo corrige sozinho o que é hábito de digitação:

- **ponto e espaço** são removidos, porque o número costuma ser lido de uma licença impressa com pontuação;
- espaço no fim, vindo de colar texto, também sai.

O campo **não** corrige, e por isso recusa:

- letra no meio do número;
- menos de cinco dígitos;
- mais de sete dígitos.

O que fazer quando ele recusar:

1. Confira o número na licença, dígito a dígito. Registro antigo tem cinco dígitos, e isso é normal: a faixa aceita os dois extremos.
2. Não complete com zeros à esquerda para chegar a sete. Zero à esquerda muda o número, e um CANAC de cinco dígitos é um número de cinco dígitos, e não um de sete mal preenchido.
3. Se o número que está na licença realmente não couber na faixa, não force o cadastro: registre o tripulante sem o CANAC, que é campo opcional, e trate a divergência do documento antes.

Vale lembrar que o CANAC é guardado cifrado, e aparece apenas para quem abre o cadastro daquela pessoa. Ele não é usado como identificador em nenhuma outra parte da tela.`,

  // ==========================================================================
  // DIARIO DE BORDO
  // ==========================================================================
  'voe-di-01': `## Diário assinado não se edita: ele se retifica

O diário de bordo é registro legal. Depois de assinado, o lançamento **não pode ser alterado**, e isso não é uma trava da tela: é a regra do próprio documento, e o sistema a mantém na origem do dado.

Por isso a ação de editar **simplesmente não aparece** ao lado de um lançamento assinado. Uma tela que desenhasse esse botão prometeria o que não cumpre: você clicaria, preencheria tudo, enviaria, e receberia um erro depois de todo o trabalho. No lugar dele aparece a ação que de fato funciona: **Retificar**.

Na coluna **Ações** do extrato, o que aparece depende do estado da linha:

- lançamento assinado e ainda não corrigido: aparece **Retificar**;
- lançamento não assinado: aparece **Não assinado**, porque rascunho se corrige nele mesmo e não precisa de retificação;
- lançamento já corrigido: aparece **Não retificável**, e quem se corrige a partir dali é a retificação mais recente.

Imutabilidade sem caminho de correção congelaria o erro, e é por isso que a retificação existe desde o primeiro dia deste diário. Ela não apaga nada: cria um lançamento novo que aponta para o antigo e carrega o motivo da correção.

Se você precisa corrigir algo que já foi assinado, o caminho é o artigo sobre como registrar uma retificação, aqui nesta mesma categoria. Se o que você quer é ajustar um lançamento que ainda não foi assinado, corrija-o diretamente, sem retificação.`,

  'voe-di-02': `## Como registrar uma retificação

1. Abra **Diário de bordo** no menu. O extrato aparece com os lançamentos do período.
2. Encontre o lançamento que precisa ser corrigido. Se ele estiver assinado e ainda não tiver sido corrigido, a coluna **Ações** mostra **Retificar**.
3. Clique em **Retificar**. Abre a janela **Retificar lançamento**, com o cabeçalho dizendo qual voo está sendo retificado.
4. Preencha os valores corretos: **Horas de voo**, **Ciclos** e **Combustível consumido (L)**. Os campos vêm com o que estava no lançamento original, e você altera o que estiver errado.
5. Preencha **Motivo da retificação**. Ele é obrigatório, e não é burocracia: fica registrado ao lado do lançamento, e é o que a autoridade lê para entender a correção. Escreva o que aconteceu, e não apenas a palavra correção. Espaços em branco não contam como motivo preenchido.
6. Clique em **Registrar retificação**. O botão passa a mostrar **Registrando** enquanto a gravação acontece.
7. Ao terminar, o extrato mostra **as duas linhas**: a original, marcada como **Retificada**, e a nova, marcada como **Retificação**, com o motivo na coluna correspondente.

Se a gravação for recusada, a janela mostra o motivo da recusa e **não fecha**, para você corrigir sem perder o que digitou. As recusas possíveis e o que fazer em cada uma estão no artigo sobre por que a retificação foi recusada.

Uma retificação por lançamento. Se for preciso corrigir de novo, retifique a **retificação mais recente**, e não o original outra vez. Sem isso, o histórico viraria um conjunto de correções concorrentes sem ordem, e numa fiscalização a única pergunta que importa é qual delas vale.`,

  'voe-di-03': `## Por que a retificação foi recusada

A janela de retificação mostra o motivo da recusa e não fecha, para você resolver sem perder o que digitou. São seis recusas possíveis, e cada uma tem uma saída própria.

**Toda retificação precisa do motivo escrito.** O campo **Motivo da retificação** está vazio, ou tem apenas espaços. Escreva o que aconteceu. Sem motivo, a correção é uma alteração disfarçada, e o documento perde justamente a parte que explica por que dois lançamentos do mesmo voo existem.

**Informe qual lançamento está sendo retificado, ou remova o motivo.** Você escreveu um motivo sem apontar o que corrige. Isso acontece quando o lançamento é original, e não uma correção. Ou aponte o lançamento a corrigir, ou apague o motivo.

**O lançamento que se pretende retificar não existe.** O lançamento original não foi encontrado. Feche a janela, recarregue o extrato e tente de novo a partir da linha que aparecer. Se ele continuar sumido, avise o suporte antes de lançar qualquer coisa por fora.

**Este lançamento ainda não foi assinado: corrija-o diretamente, sem retificação.** Rascunho se corrige nele mesmo. Criar uma retificação para ele encheria o histórico de um par sem sentido.

**A retificação precisa ser do mesmo voo do lançamento original.** A correção está apontando para um lançamento de outro voo. Feche a janela e comece de novo a partir da linha certa do extrato. Sem essa recusa, as horas migrariam de um voo para outro sem ninguém ver.

**Este lançamento já foi retificado. Retifique a retificação mais recente.** Existe apenas uma retificação por lançamento. Localize no extrato a linha marcada como **Retificação** daquele voo e corrija a partir dela.

Uma observação honesta: a tela confere o que consegue conferir antes de enviar, e mostra todos os problemas de uma vez em vez de um por vez. Mesmo assim, a palavra final é do registro do diário, e uma recusa pode chegar depois do envio. Quando isso acontecer, a mensagem vem escrita na própria janela.`,

  'voe-di-04': `## O original e a retificação ficam os dois na tela

Quando um lançamento é retificado, nada é apagado e nada é escondido. O extrato passa a mostrar **duas linhas** para aquele voo:

- a **original**, marcada na coluna Situação como **Retificada**;
- a **retificação**, marcada como **Retificação**, com o motivo na coluna **Motivo da retificação**.

Esconder a original seria reescrever a história, que é exatamente o que um diário de bordo existe para impedir. A autoridade precisa ver o que foi lançado **e** o que foi corrigido, com o motivo de cada correção.

Na coluna **Situação**, os valores que você vai encontrar são:

- **Original**: lançamento original, ainda sem correção;
- **Retificada**: lançamento original que já foi corrigido;
- **Retificação**: a linha que corrige uma anterior.

Na coluna **Ações**, o lançamento já retificado mostra **Não retificável**, porque quem se corrige a partir dali é a retificação mais recente, e não o original de novo.

Isso tem uma consequência prática ao somar horas ou ciclos a partir do extrato: as duas linhas do mesmo voo aparecem, e contar as duas dobraria o voo. Ao conferir totais na tela, use a linha que vale, que é a retificação mais recente daquele voo.

Ao ser questionado sobre uma correção, o que responde é o motivo que foi escrito no momento da retificação. Ele fica ali para sempre, ao lado do lançamento, e é a razão de o campo ser obrigatório.`,

  'voe-di-05': `## Ler o extrato do diário: colunas e filtros

O extrato mostra os lançamentos do diário de bordo em uma tabela, e traz no topo a contagem de lançamentos exibidos.

**As colunas**, na ordem em que aparecem:

- **Data**: a data do voo;
- **Voo**: o número do voo;
- **Matrícula**: a aeronave, ou um traço quando o voo não tem aeronave resolvida;
- **Horas** e **Ciclos**: o que foi lançado;
- **Combustível (L)**: o consumo, quando foi medido;
- **Assinatura**: quem assinou e quando, ou **Não assinado**;
- **Situação**: Original, Retificada ou Retificação;
- **Motivo da retificação**: preenchido apenas nas linhas de retificação;
- **Ações**: Retificar, Não assinado ou Não retificável.

**Os filtros** ficam no bloco **Filtros do diário**, acima da tabela:

- **De** e **Até** recortam por período;
- **Matrícula** recorta por aeronave, e a opção **Todas** remove o recorte.

Quando o filtro não encontra nada, a tela diz que não há lançamento naquele período ou naquela matrícula, e sugere limpar o filtro para ver todos. Isso é diferente da mensagem de que não há lançamento no diário da organização, que aparece quando ainda não existe lançamento nenhum. Uma diz que o recorte não achou; a outra diz que não há o que achar.

A data e a hora aparecem sempre no formato brasileiro, dia antes de mês, mesmo se a interface estiver em outro idioma. O diário é conferido contra documento brasileiro, e alternar o formato trocaria dia por mês em silêncio numa auditoria.

Em telas estreitas a tabela rola dentro do próprio quadro, e não empurra a página para o lado. Ainda assim, o extrato tem muitas colunas, e a consulta rende mais em tela de escritório do que no celular.`,

  'voe-di-06': `## Combustível em branco não é zero, e de onde vêm as horas da aeronave

Dois pontos do lançamento causam confusão com frequência, e os dois têm efeito no cadastro da frota.

**Combustível consumido em branco não é zero.** O campo traz a orientação escrita ao lado: deixe em branco se não foi medido, porque zero significa que mediu e deu zero. São afirmações diferentes. Zero é uma medição, e diz que a aeronave não consumiu nada naquele voo. Branco diz que ninguém mediu.

Preencher zero por hábito, quando não houve medição, insere no histórico um número que ninguém apurou. Quem for analisar consumo depois vai somar esse zero como se fosse dado, e a média sai errada sem que nada acuse o problema. Na dúvida, deixe em branco: a coluna do extrato mostra o campo vazio, e isso é a verdade.

**As horas da aeronave não são digitadas na Frota.** O que você lança de horas de voo no diário é somado às horas da aeronave. Por isso o cadastro de frota não pede que você atualize as horas de célula a cada voo: o número informado no cadastro é o ponto de partida, e a partir do primeiro lançamento ele cresce com o que o diário registra.

A consequência prática é direta: **horas lançadas errado no diário aparecem no cadastro da aeronave**, e mudam a distância para a próxima inspeção. Se você perceber que lançou horas erradas em um voo já assinado, o caminho é a retificação, e não editar o cadastro da frota para compensar. Compensar no cadastro deixaria dois documentos contando histórias diferentes, e o diário é o que a autoridade lê.

Ao retificar, informe as horas **corretas** do voo, e não a diferença. A retificação substitui o que valia, e não soma em cima.`,
};

/* ===== bloco 3-meteo ===== */
const ARTIGOS_3: ArtigoDaBase[] = [
  // ------------------------------------------------------------------------
  // METEOROLOGIA · sete artigos.
  // O `voe-me-02` e o mais importante da categoria: e ele que separa observacao de
  // previsao de modelo, que e a decisao mais delicada do produto inteiro.
  // ------------------------------------------------------------------------
  {
    id: 'voe-me-01',
    titulo: 'Os três boletins da tela: METAR, TAF e SIGMET',
    resumo: 'METAR observa, TAF prevê para o aeródromo e SIGMET avisa sobre uma área.',
    marcadores: ['metar', 'taf', 'sigmet', 'meteorologia'],
    categoriaId: 'meteorologia',
  },
  {
    id: 'voe-me-02',
    titulo: 'Previsão de modelo não é observação de aeródromo',
    resumo: 'A diferença não está no número, está na origem. Como reconhecer cada uma na tela.',
    marcadores: ['metar', 'previsão', 'modelo', 'contingência', 'meteorologia'],
    categoriaId: 'meteorologia',
  },
  {
    id: 'voe-me-03',
    titulo: 'Os cinco selos de procedência, e o que cada um diz',
    resumo: 'Todo boletim chega com um selo antes do texto. Leia o selo antes de ler o número.',
    marcadores: ['procedência', 'selo', 'meteorologia'],
    categoriaId: 'meteorologia',
  },
  {
    id: 'voe-me-04',
    titulo: 'O texto original é o que vale, a tradução é apoio',
    resumo: 'A leitura em português ajuda a achar mais rápido. Quem despacha confere no original.',
    marcadores: ['metar', 'taf', 'tradução', 'meteorologia'],
    categoriaId: 'meteorologia',
  },
  {
    id: 'voe-me-05',
    titulo: 'Observação vencida: por que ela continua na tela',
    resumo: 'Boletim fora da validade não some. Aparece com a palavra VENCIDA e a hora da captura.',
    marcadores: ['validade', 'cache', 'meteorologia'],
    categoriaId: 'meteorologia',
  },
  {
    id: 'voe-me-06',
    titulo: 'Quando a meteorologia aparece como indisponível',
    resumo: 'São quatro motivos diferentes, e cada um pede uma ação diferente de quem opera.',
    marcadores: ['indisponível', 'ausência', 'meteorologia'],
    categoriaId: 'meteorologia',
  },
  {
    id: 'voe-me-07',
    titulo: 'A hora da consulta não é a hora do boletim',
    resumo: 'Um METAR das 12:00 consultado às 14:30 continua sendo o das 12:00. Onde ler cada uma.',
    marcadores: ['metar', 'hora', 'idade', 'meteorologia'],
    categoriaId: 'meteorologia',
  },

  // ------------------------------------------------------------------------
  // NOTAM E AERODROMOS · oito artigos.
  // O `voe-nt-02` e o centro da categoria: e ele que separa "nao ha aviso" de "nao
  // consegui consultar", que e o defeito mais caro que esta tela poderia ter.
  // ------------------------------------------------------------------------
  {
    id: 'voe-nt-01',
    titulo: 'O que é um NOTAM, e por que ele não é resumido',
    resumo: 'Aviso oficial de pista fechada, auxílio fora do ar ou obstáculo novo.',
    marcadores: ['notam', 'aviso', 'aeródromo'],
    categoriaId: 'notam',
  },
  {
    id: 'voe-nt-02',
    titulo: 'Lista vazia de NOTAM: as duas mensagens que nunca se confundem',
    resumo: 'Sem aviso vigente e falha na consulta produzem a mesma lista vazia e dizem o oposto.',
    marcadores: ['notam', 'ausência', 'consulta', 'aeródromo'],
    categoriaId: 'notam',
  },
  {
    id: 'voe-nt-03',
    titulo: 'Quando a tela diz que não foi possível consultar',
    resumo: 'A consulta não respondeu. Ninguém sabe se há aviso, e a tela avisa isso em destaque.',
    marcadores: ['notam', 'falha', 'indisponível'],
    categoriaId: 'notam',
  },
  {
    id: 'voe-nt-04',
    titulo: 'Vigência do NOTAM: início, fim e sem término publicado',
    resumo: 'Aviso sem data de fim não é aviso com data desconhecida. A tela escreve a diferença.',
    marcadores: ['notam', 'vigência', 'permanente'],
    categoriaId: 'notam',
  },
  {
    id: 'voe-nt-05',
    titulo: 'Por que o NOTAM não tem fonte de contingência',
    resumo: 'A meteorologia tem previsão de modelo. Para NOTAM não existe equivalente.',
    marcadores: ['notam', 'contingência', 'fonte'],
    categoriaId: 'notam',
  },
  {
    id: 'voe-nt-06',
    titulo: 'Encontrar um aeródromo no catálogo',
    resumo: 'A busca aceita código ICAO, nome ou cidade, e filtra a tabela enquanto você digita.',
    marcadores: ['aeródromo', 'catálogo', 'icao', 'busca'],
    categoriaId: 'notam',
  },
  {
    id: 'voe-nt-07',
    titulo: 'Aeródromo próprio: pista sem publicação oficial',
    resumo: 'Pista particular costuma não ter METAR nem NOTAM. Consulta vazia ali não é céu limpo.',
    marcadores: ['aeródromo', 'próprio', 'catálogo'],
    categoriaId: 'notam',
  },
  {
    id: 'voe-nt-08',
    titulo: 'Quando o catálogo de aeródromos aparece vazio',
    resumo: 'Sem ao menos um código ICAO não há o que consultar, e a tela trata isso como falha.',
    marcadores: ['aeródromo', 'catálogo', 'indisponível'],
    categoriaId: 'notam',
  },
];

/**
 * OS CORPOS, em markdown, indexados pelo id do artigo.
 *
 * Separados da lista de proposito: a lista e carregada inteira para desenhar a busca, e o
 * corpo so e buscado ao abrir um artigo. Juntos, abrir a Central custaria o texto dos
 * quinze artigos de uma vez.
 */
const CORPOS_3: Record<string, string> = {
  'voe-me-01': `## Os três boletins da tela: METAR, TAF e SIGMET

A tela de Meteorologia mostra três tipos de boletim, e eles respondem perguntas diferentes. Ler os três como se fossem a mesma coisa é o engano mais comum de quem abre esta tela pela primeira vez.

**METAR** é observação. Alguém, ou um sensor homologado, mediu o que está acontecendo naquele aeródromo naquele momento e publicou. É o que descreve o presente.

**TAF** é previsão oficial do aeródromo, feita para um período à frente. Continua sendo documento de fonte oficial, e continua não descrevendo o agora: ele descreve o que se espera.

**SIGMET** é aviso de fenômeno significativo em uma área, e não em um ponto. Ele não fala de um aeródromo específico: fala de uma região do espaço aéreo por onde a rota pode passar.

O filtro no topo permite ver um tipo por vez. Com o filtro em todos, a lista traz os três misturados, e é por isso que cada cartão mostra o tipo em destaque antes do texto: sem essa marca, uma previsão de amanhã pareceria condição atual.

Nenhum dos três, sozinho ou somado aos outros, decide se o voo sai. A tela reúne e organiza o que as fontes publicaram. A decisão é do comandante.`,

  'voe-me-02': `## Previsão de modelo não é observação de aeródromo

Esta é a distinção mais importante de toda a tela de Meteorologia, e ela existe porque as duas coisas se parecem quando desenhadas: as duas trazem vento, visibilidade e teto, em números com a mesma cara.

A diferença não está no formato do número, está na origem dele. Um METAR foi medido naquele aeródromo, por alguém ou por sensor homologado. Uma previsão de modelo foi calculada por um modelo numérico para uma área, e o cálculo pode estar certo ou errado sem que o número mude de aparência.

Origem não se vê olhando o número, e por isso ela vem escrita. Quando o boletim vier de modelo, o selo acima do texto diz, com todas as letras, PREVISÃO DE MODELO, não é observação de aeródromo, seguido do nome do modelo que a produziu. O nome do modelo aparece sempre, porque saber quem previu é o que permite julgar o quanto confiar.

A fonte de modelo só entra quando a fonte oficial não respondeu. Ela existe para a tela não ficar em branco, e não para substituir a observação. Para despacho, ela não ocupa o lugar do METAR.

Se houver qualquer boletim de modelo na consulta, a tela avisa uma vez no topo, com a contagem. Esse aviso existe porque quem rola até o terceiro cartão pode não ter visto o selo do primeiro.

Um boletim de modelo que chegue sem o nome do modelo não é mostrado: ele vira indisponível, e a tela diz o motivo. Perder um boletim custa uma informação a menos. Mostrar previsão sem dizer que é previsão custa uma decisão tomada sobre estimativa que parecia medição, e os dois custos não se comparam.

A decisão de voar continua sendo do comandante, e esta tela não a substitui nem a recomenda.`,

  'voe-me-03': `## Os cinco selos de procedência, e o que cada um diz

Todo boletim desta tela chega com um selo colorido acima do texto. Ele vem antes do texto de propósito: quem lê de cima para baixo precisa saber a origem do dado antes de ler o dado, porque depois já leu.

**Observação oficial, ao vivo.** A consulta à fonte oficial respondeu agora, e o selo traz a hora. É observação do aeródromo, e é o estado mais forte que a tela tem.

**Observação oficial, guardada.** O boletim veio de uma consulta anterior e ainda está dentro da validade. Continua sendo observação real do aeródromo, apenas não é desta hora, e a hora da captura vem junto.

**Observação VENCIDA, capturada em.** Há boletim, e ele passou da validade. A palavra vencida vem antes da hora justamente para ser lida primeiro. Ele continua na tela porque boletim velho ainda informa, desde que a idade dele esteja escrita.

**PREVISÃO DE MODELO, não é observação de aeródromo.** O boletim veio de um modelo numérico, com o nome do modelo ao lado. Este selo tem artigo próprio nesta base, porque é a distinção mais delicada da tela.

**Indisponível.** Não há o que mostrar, e o selo traz o motivo. A tela não preenche o buraco com valor estimado.

Os cinco existem para que nenhum boletim apareça sem que a pessoa saiba de onde ele veio. Não há caminho na tela em que um dado apareça sem selo, e isso é garantido pela forma como a tela é construída, não pela lembrança de quem a desenhou.`,

  'voe-me-04': `## O texto original é o que vale, a tradução é apoio

Cada cartão de boletim traz dois blocos de texto. O primeiro tem a etiqueta Texto original, é este que vale, e mostra o código como a fonte oficial publicou. O segundo, quando existe, tem a etiqueta Leitura em português, apoio à consulta.

A ordem e as etiquetas não são decoração. Dois blocos de texto lado a lado, sem marca, têm o mesmo peso visual, e a tendência natural é ler o mais fácil, que é a tradução. A etiqueta existe para dizer qual dos dois sustenta uma decisão.

O código original aparece em fonte monoespaçada e com as quebras preservadas. METAR e TAF são códigos em que o agrupamento dos campos por espaço faz parte da leitura, e fonte proporcional embaralha grupos que o olho treinado separa pela posição.

A tradução é produzida por inteligência artificial e serve para achar mais rápido o que ler. Ela não classifica, não conclui e não autoriza nada. Uma tradução com imprecisão não muda a decisão, porque o código bruto está ali, na mesma tela, marcado como o que vale.

Um boletim pode aparecer sem tradução, e isso é normal: sem tradução ele ainda serve para consulta. O contrário não existe nesta tela: nenhum boletim aparece só traduzido, e não há caminho que produza isso.

Quem despacha lê o original. A tradução ajuda a chegar nele mais depressa.`,

  'voe-me-05': `## Observação vencida: por que ela continua na tela

Um boletim meteorológico envelhece. Passada a validade, ele deixa de descrever o presente, e a tela precisa escolher entre duas saídas: escondê-lo ou mostrá-lo dizendo a idade.

Esta tela mostra. O selo traz a palavra VENCIDA antes de qualquer número, e logo depois a hora real da captura, que é a hora em que a consulta aconteceu de verdade, e nunca a hora em que a tela foi desenhada.

A razão é prática. Um boletim de duas horas atrás ainda informa alguma coisa sobre o aeródromo, e apagá-lo deixaria a tela vazia num momento em que existe informação disponível. O que não pode acontecer é ele aparecer com cara de atual.

Por isso a idade nunca é omitida neste estado. Dado velho pode ser mostrado. Dado velho sem a idade ao lado não, porque quem lê assume que é atual, e essa é a forma mais silenciosa de errar num sistema de operação aérea.

Quando a tela não consegue saber de quando o boletim é, ela não o mostra como vencido nem como atual: ela o trata como indisponível, com o motivo escrito. Não saber a idade é diferente de saber que está velho, e as duas coisas levam a ações diferentes.

Um boletim vencido não impede nem autoriza nada. Ele é uma informação com data, e o peso que ela tem na decisão é de quem decide.`,

  'voe-me-06': `## Quando a meteorologia aparece como indisponível

Indisponível significa que não há boletim para mostrar, e a tela traz junto o motivo. O motivo importa porque cada um leva a uma ação diferente, e um texto único apagaria essa diferença.

**A consulta nunca aconteceu.** A fonte não foi chamada, ou a chamada não voltou. Não se sabe nada sobre o aeródromo, e vale procurar a informação por outro caminho oficial.

**Consultamos, e o aeródromo não tem boletim publicado.** Este é um fato apurado: a pergunta foi feita e a resposta foi que não há. É diferente do caso acima, e a tela não mistura os dois.

**Há boletim, e não há como saber de quando ele é.** O dado existe e a data dele não pôde ser lida. A tela prefere não mostrar a mostrar sem idade, porque um boletim sem idade é lido como atual.

**Veio previsão de modelo sem dizer de qual modelo.** O boletim alternativo chegou sem o rótulo obrigatório, e a tela recusa desenhá-lo. Mostrar previsão sem dizer que é previsão custaria mais do que perder aquele boletim.

Em nenhum dos quatro a tela inventa um valor para preencher o espaço. Número em tela ou é real ou é um traço, e ausência de dado é sempre dita, nunca preenchida.

Se a tela ficar indisponível repetidamente para o mesmo aeródromo, o caminho é avisar quem cuida da operação, e não insistir na recarga.`,

  'voe-me-07': `## A hora da consulta não é a hora do boletim

São duas horas diferentes, e confundi-las é o engano clássico com este tipo de dado.

A hora que aparece no selo é a hora da consulta: o momento em que este sistema perguntou à fonte e recebeu resposta. Ela serve para você saber a idade do que está vendo na tela.

A hora de emissão do boletim é outra, e ela está dentro do próprio texto original, publicada pela fonte oficial no formato padrão. Um METAR emitido às 12:00 e consultado às 14:30 continua sendo o das 12:00, mesmo que o selo mostre 14:30.

Na prática, isso significa que um selo recente não garante um boletim recente. O selo garante que a conversa com a fonte foi recente. Para saber de quando é a observação, a leitura é no código original.

Quando a tela não consegue medir a idade da consulta, ela não escreve zero minutos. Zero afirmaria uma captura recente que nunca aconteceu, e neste produto a ausência de medição é dita como ausência.

Se a hora aparecer adiantada em relação ao relógio da parede, isso indica desalinhamento de relógio entre as máquinas, e não um boletim vindo do futuro. Vale reportar, porque a idade de todos os boletins passa a ficar errada ao mesmo tempo.`,

  'voe-nt-01': `## O que é um NOTAM, e por que ele não é resumido

NOTAM é o aviso oficial de que algo mudou num aeródromo ou no espaço aéreo: pista fechada, auxílio à navegação fora do ar, obstáculo novo, área restrita ativada. É documento de fonte oficial, publicado em formato padronizado.

Cada aviso aparece em um cartão, com o número publicado pela autoridade no topo, por exemplo A1234/26. É esse número que se cita num briefing, e é por ele que o aviso é encontrado em qualquer outra fonte. Ele é único por definição da autoridade, então não há dois avisos vigentes com o mesmo número.

Abaixo do número vem a vigência, e depois o texto. O original aparece com a etiqueta Texto original, é este que vale, em fonte monoespaçada e com as quebras preservadas, porque NOTAM é publicado em campos separados por posição e esse agrupamento faz parte da leitura.

Quando existe, a leitura em português aparece logo abaixo, marcada como apoio à consulta. Ela é produzida por inteligência artificial e nunca substitui o texto que a autoridade publicou.

Esta tela não classifica NOTAM como relevante ou irrelevante, e não esconde nenhum por parecer pouco importante. Um aviso que a tela escondesse por parecer pequeno é exatamente o aviso que apareceria no relatório do acidente.

A leitura e a decisão são de quem opera. A tela reúne, organiza e mostra.`,

  'voe-nt-02': `## Lista vazia de NOTAM: as duas mensagens que nunca se confundem

Esta é a confusão que a tela de NOTAM existe para impedir, e ela seria o erro mais caro que este produto poderia cometer.

Duas situações completamente diferentes terminam sem nenhum aviso na tela. Elas se pareceriam, porque as duas produzem uma lista vazia. Por isso elas nunca aparecem com o mesmo texto.

**Não há NOTAM vigente para este aeródromo.** A consulta foi feita, respondeu, e o resultado é que não há aviso publicado. Isso é informação. A frase vem acompanhada de quando a consulta aconteceu, porque uma resposta de três horas atrás não vale o mesmo que uma de agora.

**Não foi possível consultar os NOTAM deste aeródromo.** A consulta não aconteceu ou falhou. Ninguém sabe nada sobre aquele aeródromo. Pode haver pista fechada, auxílio fora do ar ou obra, e nada disso apareceria na tela.

A segunda mensagem vem em destaque, com o aviso escrito logo abaixo: isto não significa que o aeródromo está sem restrição. A frase existe porque a leitura natural de uma tela vazia é tranquilizadora, e nesse caso a tranquilidade seria falsa.

O jeito de distinguir as duas é ler a frase, e não contar os cartões. A quantidade é zero nos dois casos. Só o texto diz qual deles você está vendo.

Ausência de aviso é um fato, e só pode ser afirmada quando foi apurada. Falha de consulta é a falta do fato. A tela diz as duas coisas com todas as letras, e nunca deixa o silêncio da fonte parecer permissão.`,

  'voe-nt-03': `## Quando a tela diz que não foi possível consultar

A mensagem aparece em destaque, com borda de alerta, e traz três partes. A primeira diz que a consulta não foi possível. A segunda diz o que isso não significa. A terceira, em fonte de máquina, traz o motivo.

A segunda parte é a mais importante: isto não significa que o aeródromo está sem restrição. Significa que a consulta não respondeu, e pode haver aviso vigente que não está sendo mostrado ali.

O motivo aparece porque ele muda o que fazer. Fonte não configurada, serviço fora do ar e resposta sem data são situações diferentes, e nenhuma delas se resolve recarregando a tela indefinidamente.

O que fazer nesse estado é buscar a informação por outro caminho oficial antes de qualquer planejamento que dependa daquele aeródromo, e avisar quem cuida da operação de que a consulta está falhando.

O que não fazer é tratar a tela como se ela tivesse dito que não há avisos. Ela não disse isso. Ela disse que não sabe, e a diferença entre as duas frases é o motivo de esta tela existir.

Esta tela não libera nem impede voo. Ela informa o que conseguiu apurar, e diz claramente quando não conseguiu apurar nada.`,

  'voe-nt-04': `## Vigência do NOTAM: início, fim e sem término publicado

Cada cartão mostra a vigência logo abaixo do número do aviso. Ela aparece de duas formas, e a diferença entre elas muda a atenção que o aviso pede.

A forma comum traz início e fim, com data e hora. O aviso vale entre os dois, e os dois limites entram: um NOTAM que começa às 10:00 está valendo às 10:00, e um que termina às 18:00 ainda vale às 18:00.

A outra forma traz o início seguido de sem término publicado. Isso significa aviso permanente, ou sem data de fim publicada pela autoridade. Não significa data desconhecida, e não significa erro de leitura.

A diferença importa porque um aviso sem fim declarado exige atenção diferente de um que termina hoje à noite. Obstáculo fixo e restrição que não muda são justamente os que valem por tempo indeterminado, e seriam os primeiros a se perder se fossem tratados como vencidos.

Quando a data de um aviso não pode ser lida, ele não é escondido. A dúvida se resolve para o lado de mostrar: esconder um aviso que talvez esteja valendo custa uma restrição que ninguém viu, e mostrar um que talvez já tenha vencido custa uma leitura a mais.

As horas aparecem no formato brasileiro de 24 horas, independentemente de como a máquina esteja configurada.

A validade da consulta é outra coisa e não se confunde com a vigência do aviso: uma diz há quanto tempo perguntamos, a outra diz por quanto tempo o aviso vale.`,

  'voe-nt-05': `## Por que o NOTAM não tem fonte de contingência

A tela de Meteorologia tem uma fonte alternativa: quando a fonte oficial não responde, ela pode mostrar previsão de modelo, sempre rotulada como tal. A tela de NOTAM não tem equivalente, e essa ausência é deliberada.

Não existe modelo que preveja que uma pista será fechada. Restrição de aeródromo é ato publicado, e não fenômeno calculável. Não há segunda fonte capaz de produzir um aviso por estimativa.

Se existisse, os dois lados do erro seriam caros. Inventar restrição que não existe cancela voo bom. Inventar a ausência de restrição libera voo que não deveria sair, e esse é o pior dos dois de longe.

Por isso, aqui, ou a fonte oficial respondeu, ou não há o que dizer. A tela nunca preenche o espaço com o que provavelmente vale.

Na prática, isso significa que a tela de NOTAM tem menos estados que a de meteorologia, e que a falha de consulta aparece de forma mais dura. É proposital: a frase mais tranquilizadora que este produto consegue exibir é a de que não há aviso, e ela só pode aparecer quando for verdade apurada.`,

  'voe-nt-06': `## Encontrar um aeródromo no catálogo

A consulta de NOTAM e a de meteorologia se fazem por código ICAO, o código de quatro letras do aeródromo. O catálogo existe para você chegar até esse código quando não o tem de cabeça.

O campo Buscar aeródromo filtra a tabela abaixo enquanto você digita, e aceita três coisas: o código ICAO, o nome do aeródromo ou a cidade. Buscar pela cidade resolve o caso mais comum, que é saber para onde se vai e não lembrar o código.

A tabela mostra ICAO, nome, cidade e UF. Clicar em uma linha seleciona aquele aeródromo, e a consulta de avisos acima passa a ser dele.

No seletor do topo, o código ICAO vem antes do nome. É por ele que se procura no despacho, e vê-lo primeiro encurta a busca visual.

A busca sem resultado e o catálogo vazio dizem coisas diferentes, e a tela usa frases diferentes para as duas. Nenhum aeródromo corresponde à busca significa que o termo digitado não achou nada, e apagar o termo devolve a lista inteira. Nenhum aeródromo cadastrado significa que não há catálogo, e é outro problema, tratado em artigo próprio.

Trocar de aeródromo refaz a consulta de avisos, e o resultado passa a ser do aeródromo novo.`,

  'voe-nt-07': `## Aeródromo próprio: pista sem publicação oficial

O catálogo reúne dois tipos de aeródromo. Os do catálogo compartilhado são os publicados oficialmente. Os próprios são os cadastrados pelo operador, e costumam ser pistas particulares.

A diferença muda como se lê uma consulta que volta vazia. Pista particular sem publicação oficial normalmente não tem METAR e não tem NOTAM, porque não há quem os publique para ela.

Nesse caso, uma consulta sem resultado não está dizendo que o tempo está bom nem que não há restrição. Está dizendo que aquela pista não é coberta pelas fontes oficiais consultadas.

Confundir isso com céu limpo é o mesmo erro de confundir falha de consulta com ausência de aviso, e o custo é o mesmo: alguém planeja como se soubesse, quando não sabe.

Para pista particular, a informação de condição e de restrição vem por outro caminho, normalmente de quem opera a pista. Esta tela não tem como suprir isso, e não finge ter.

Quando a rota depende de um aeródromo próprio, vale confirmar as condições diretamente na origem antes de qualquer planejamento.`,

  'voe-nt-08': `## Quando o catálogo de aeródromos aparece vazio

Para quase toda lista deste produto, vazio é um fato apurado: perguntamos, e não há registro. Para o catálogo de aeródromos, vazio não é tratado assim, e a diferença é deliberada.

A razão é que sem ao menos um código ICAO não há o que consultar. As telas de meteorologia e de NOTAM ficam sem função, porque as duas perguntam por código de aeródromo. Um catálogo sem nenhum aeródromo não é um estado normal de operação.

Por isso a tela trata o catálogo vazio como indisponível, e não como lista vazia. A mensagem diz o que foi observado, que o catálogo voltou sem registro, e a consequência, que não há o que consultar.

A mensagem não afirma a causa, de propósito. Pode ser falta de permissão de leitura, pode ser catálogo ainda não carregado, e a diferença é de quem cuida do sistema, não de quem opera.

Nesse estado não há botão de repetir, e a ausência dele é intencional: repetir não adianta em nenhuma das causas possíveis, e um botão que insiste contra uma recusa que não vai ceder é uma promessa falsa.

O caminho é avisar quem cuida da operação. Enquanto o catálogo não voltar, as duas consultas ficam sem entrada, e nenhuma informação de aeródromo pode ser obtida por esta tela.`,
};

/* ===== bloco 4-seguranca ===== */
const ARTIGOS_4: ArtigoDaBase[] = [
  // -- Segurança operacional ------------------------------------------------------------
  {
    id: 'voe-sg-01',
    titulo: 'Os quatro tipos de ocorrência, e por que não são a mesma coisa',
    resumo: 'Incidente, quase acidente, condição insegura e observação respondem a perguntas diferentes.',
    marcadores: ['ocorrencia', 'tipo', 'incidente', 'quase acidente', 'condicao insegura', 'seguranca'],
    categoriaId: 'seguranca',
  },
  {
    id: 'voe-sg-02',
    titulo: 'Como registrar uma ocorrência',
    resumo: 'O caminho completo do registro, campo por campo, e o que fazer quando o salvamento recusa.',
    marcadores: ['ocorrencia', 'registrar', 'relato', 'seguranca'],
    categoriaId: 'seguranca',
  },
  {
    id: 'voe-sg-03',
    titulo: 'Relato anônimo: o que o sistema garante e o que ele não garante',
    resumo: 'Marcado como anônimo, seu nome não aparece para leitor nenhum, em tela nenhuma.',
    marcadores: ['anonimo', 'sigilo', 'relato', 'ocorrencia', 'seguranca'],
    categoriaId: 'seguranca',
  },
  {
    id: 'voe-sg-04',
    titulo: 'Gravidade e tipo são eixos independentes',
    resumo: 'Um quase acidente pode ser de gravidade alta, e um incidente pode ser de gravidade baixa.',
    marcadores: ['gravidade', 'tipo', 'classificacao', 'ocorrencia', 'seguranca'],
    categoriaId: 'seguranca',
  },
  {
    id: 'voe-sg-05',
    titulo: 'Tratar uma ocorrência: da abertura ao encerramento',
    resumo: 'Como registrar a análise, para onde a ocorrência vai depois dela, e por que não volta a aberta.',
    marcadores: ['tratamento', 'analise', 'encerrar', 'status', 'seguranca'],
    categoriaId: 'seguranca',
  },
  {
    id: 'voe-sg-06',
    titulo: 'Encontrar uma ocorrência na lista',
    resumo: 'Os filtros da tela recortam por situação: todas, aberta, em análise e encerrada.',
    marcadores: ['filtro', 'buscar', 'lista', 'ocorrencia', 'seguranca'],
    categoriaId: 'seguranca',
  },

  // -- Conformidade ---------------------------------------------------------------------
  {
    id: 'voe-cf-01',
    titulo: 'Item obrigatório trava a conclusão do checklist',
    resumo: 'Enquanto um item obrigatório não for respondido, a execução não conclui. O opcional não trava.',
    marcadores: ['obrigatorio', 'checklist', 'concluir', 'conformidade'],
    categoriaId: 'conformidade',
  },
  {
    id: 'voe-cf-02',
    titulo: 'Executar um checklist: responder, salvar e concluir',
    resumo: 'Salvar o andamento e concluir são gestos diferentes, e você pode parar no meio sem perder nada.',
    marcadores: ['executar', 'checklist', 'salvar', 'concluir', 'conformidade'],
    categoriaId: 'conformidade',
  },
  {
    id: 'voe-cf-03',
    titulo: 'Sim, não e sem resposta são três estados, e não dois',
    resumo: 'Item não respondido não é item não conforme: um diz que ninguém olhou, o outro é constatação.',
    marcadores: ['resposta', 'nao conforme', 'checklist', 'conformidade'],
    categoriaId: 'conformidade',
  },
  {
    id: 'voe-cf-04',
    titulo: 'A ordem dos itens do checklist, e por que ela aparece na tela',
    resumo: 'Cada item tem número próprio, a sequência é sempre a mesma, e é por ele que a equipe conversa.',
    marcadores: ['ordem', 'item', 'checklist', 'conformidade'],
    categoriaId: 'conformidade',
  },
  {
    id: 'voe-cf-05',
    titulo: 'O checklist vinculado a uma norma, e quem avalia a conformidade',
    resumo: 'A tela mostra a norma ao lado para você conferir. Quem conclui a conformidade é uma pessoa.',
    marcadores: ['norma', 'avaliacao', 'checklist', 'auditoria', 'conformidade'],
    categoriaId: 'conformidade',
  },
  {
    id: 'voe-cf-06',
    titulo: 'Consultar execuções anteriores de um checklist',
    resumo: 'O histórico mostra quando cada execução ocorreu, quem executou e se foi concluída.',
    marcadores: ['historico', 'execucao', 'checklist', 'conformidade'],
    categoriaId: 'conformidade',
  },

  // -- Normas ---------------------------------------------------------------------------
  {
    id: 'voe-nr-01',
    titulo: 'Consultar uma norma na base',
    resumo: 'Como achar a norma pelo código, o que a ficha traz, e como chegar ao texto oficial.',
    marcadores: ['norma', 'consultar', 'ficha', 'buscar'],
    categoriaId: 'normas',
  },
  {
    id: 'voe-nr-02',
    titulo: 'Por que o texto da norma não é traduzido',
    resumo: 'Código, título, órgão e corpo saem exatamente como a autoridade publica, em qualquer idioma.',
    marcadores: ['traducao', 'idioma', 'norma', 'citacao'],
    categoriaId: 'normas',
  },
  {
    id: 'voe-nr-03',
    titulo: 'Ficha ainda não redigida não é norma sem conteúdo',
    resumo: 'A norma está cadastrada e o resumo dela ainda não foi escrito. A tela diz isso com todas as letras.',
    marcadores: ['ficha', 'vazio', 'norma', 'redacao'],
    categoriaId: 'normas',
  },
  {
    id: 'voe-nr-04',
    titulo: 'Perguntar às normas, e o que fazer com a resposta',
    resumo: 'A resposta vem marcada como sugestão e citando a fonte. Confira contra a norma antes de usar.',
    marcadores: ['pergunta', 'ia', 'norma', 'fonte', 'sugestao'],
    categoriaId: 'normas',
  },
  {
    id: 'voe-nr-05',
    titulo: 'Citar uma norma num briefing ou numa auditoria',
    resumo: 'Cite o código e o trecho como estão na ficha, e confira contra o texto oficial antes.',
    marcadores: ['citacao', 'auditoria', 'briefing', 'norma'],
    categoriaId: 'normas',
  },

  // -- IA: o que ela faz e o que não faz -------------------------------------------------
  {
    id: 'voe-ia-01',
    titulo: 'O que a IA faz no VOE',
    resumo: 'Traduz boletins, organiza ocorrências, ajuda a escrever plano e responde sobre norma com fonte.',
    marcadores: ['ia', 'traducao', 'metar', 'taf', 'notam', 'plano'],
    categoriaId: 'ia',
  },
  {
    id: 'voe-ia-02',
    titulo: 'O que a IA não faz, e de quem é a decisão',
    resumo: 'Ela não decide se o voo sai, não recomenda decolagem, não calcula e não assina. A decisão é sua.',
    marcadores: ['ia', 'limite', 'decisao', 'comandante'],
    categoriaId: 'ia',
  },
  {
    id: 'voe-ia-03',
    titulo: 'Resposta sem fonte citada não é exibida',
    resumo: 'Afirmação sobre norma sem a fonte que a sustenta não pode ser conferida, então a tela recusa mostrar.',
    marcadores: ['ia', 'fonte', 'norma', 'resposta'],
    categoriaId: 'ia',
  },
  {
    id: 'voe-ia-04',
    titulo: 'Como reconhecer uma saída de IA na tela',
    resumo: 'Toda saída de IA leva a marca de sugestão antes do texto, e as fontes logo abaixo dele.',
    marcadores: ['ia', 'sugestao', 'marca', 'tela'],
    categoriaId: 'ia',
  },
  {
    id: 'voe-ia-05',
    titulo: 'A cota mensal de uso da IA',
    resumo: 'Cada organização tem uma cota mensal própria de uso da IA, e ela não é compartilhada.',
    marcadores: ['ia', 'cota', 'limite', 'uso', 'mensal'],
    categoriaId: 'ia',
  },
];

/**
 * O corpo de cada artigo, em Markdown, pela chave do identificador.
 *
 * CORPO SEPARADO DA LISTA de propósito: a lista é carregada inteira para desenhar títulos e
 * resumos, e o corpo só é buscado quando alguém abre o artigo. Guardá-los juntos faria toda
 * abertura da base carregar 22 textos longos para mostrar 22 linhas.
 */
const CORPOS_4: Record<string, string> = {
  'voe-sg-01': `## Os quatro tipos de ocorrência, e por que não são a mesma coisa

Ao registrar uma ocorrência você escolhe entre quatro tipos, e eles não são graus da mesma coisa com cor diferente. Cada um responde a uma pergunta distinta, e a escolha na entrada define o que a análise consegue aprender depois.

**Incidente** é o que aconteceu e teve consequência. O evento ocorreu, e alguma coisa resultou dele.

**Quase acidente** é o que não aconteceu, e faltou pouco. Ele é o registro que mais ensina, porque entrega o aprendizado de um acidente sem o custo dele. Se você classificar um quase acidente como incidente de gravidade baixa, o registro mais valioso da base some do radar: quem varre a lista procurando o que doeu passa por cima dele.

**Condição insegura** é quando nada aconteceu ainda e existe uma condição que pode causar. É o único tipo que fala do futuro, e é ele que permite agir antes.

**Observação** é algo que você viu e vale registrar, sem alegar risco. Use quando a situação não se enquadra nos três acima e ainda assim merece ficar escrito.

A tela mostra a pergunta que cada tipo responde enquanto você escolhe, no próprio formulário, e repete essa explicação no detalhe do registro. Ela aparece ali porque quem lê um registro alheio precisa saber o que aquela classificação afirma. Sem isso, é fácil ler "quase acidente" como um incidente pequeno, que é exatamente o oposto do que ele é.

Na dúvida entre dois tipos, escolha o que descreve o que de fato ocorreu, e explique o resto no relato. O relato é texto livre e cabe tudo; o tipo é o que organiza a base.`,

  'voe-sg-02': `## Como registrar uma ocorrência

1. Abra **Segurança** no menu.
2. Clique em **Registrar ocorrência**. O formulário abre sobre a lista.
3. Preencha o **Título**. Ele é obrigatório e não pode ficar em branco.
4. Escreva o **Relato**. Também é obrigatório, e é o campo mais importante do registro: descreva o que aconteceu, onde, e o que você observou.
5. Escolha o **Tipo**. Enquanto você escolhe, a tela mostra a pergunta que cada tipo responde. Se tiver dúvida, veja o artigo sobre os quatro tipos.
6. Escolha a **Gravidade**: baixa, média ou alta.
7. Informe **Ocorrido em**, com data e hora. Repare que este campo é quando o fato aconteceu, e não quando você está registrando. Os dois costumam ser diferentes.
8. Se quiser relatar sem se identificar, marque **Anônimo** antes de salvar. Leia o artigo sobre relato anônimo para saber o que essa marca garante.
9. Preencha **Voo**, **Aeronave** e **Aeródromo** quando houver relação. Os três são opcionais: uma condição insegura de solo pode não ter voo nenhum associado, e deixar em branco é legítimo.
10. Salve.

**Se o salvamento recusar por campo vazio**, confira o título e o relato. Um campo com apenas espaços conta como vazio, mesmo parecendo preenchido na tela. Apague os espaços e escreva o texto.

**Se aparecer uma mensagem do servidor**, ela vem escrita como chegou, sem tradução. O que você digitou continua na tela e não se perde: corrija o que a mensagem apontar e salve de novo.

Registre mesmo quando parecer pequeno. Uma base de segurança vale pelo volume de coisas pequenas que alguém teve o trabalho de escrever, e não pelos poucos eventos grandes que ninguém teria como esquecer.`,

  'voe-sg-03': `## Relato anônimo: o que o sistema garante e o que ele não garante

Ao registrar uma ocorrência você pode marcar **Anônimo**. Marcada, a caixa faz com que seu nome não seja mostrado como autor do relato em lugar nenhum do sistema: nem na lista, nem no detalhe, nem para quem tem perfil de gestão.

Isso é uma decisão estrutural, e não uma preferência de exibição. Um sistema de relato voluntário só funciona enquanto quem relata acredita que não será identificado. Mostrar o autor "só para quem é gestor" desfaria a promessa inteira, porque quem relata não tem como saber quem está olhando a tela naquele momento.

**O que o sistema garante:** o campo de autor não aparece para leitor nenhum quando o relato está marcado como anônimo. A regra que decide isso mora num lugar só do sistema, e não em cada tela, justamente para que nenhuma tela possa esquecer de consultá-la.

**O que o sistema não garante:** ele não apaga o conteúdo do que você escreveu. Se o relato descrever uma situação que só uma pessoa poderia ter presenciado, o texto pode identificar você mesmo com a caixa marcada. Escreva o necessário para a análise, e evite detalhe que sirva apenas para apontar quem estava ali.

**Registro sem autor não é a mesma coisa que registro anônimo.** Existe um terceiro caso, em que o autor simplesmente não foi gravado, e o sistema mostra isso como não registrado, e não como anônimo. A diferença importa: anônimo é uma escolha que alguém fez, e a tela não promete sigilo sobre um registro em que ninguém pediu sigilo.

Se você precisa que alguém possa voltar a falar com você sobre o relato, não marque a caixa. Um relato identificado permite que a análise faça perguntas, e isso costuma melhorar a apuração.`,

  'voe-sg-04': `## Gravidade e tipo são eixos independentes

O tipo diz **o que foi**. A gravidade diz **quão grave**. As duas classificações são independentes, e o sistema não deriva uma da outra.

Isso significa que todas estas combinações são legítimas e você vai encontrá-las na base:

- Um **quase acidente de gravidade alta**: não aconteceu, e o que quase aconteceu seria grave.
- Um **incidente de gravidade baixa**: aconteceu, com consequência pequena.
- Uma **condição insegura de gravidade alta**: nada ocorreu ainda, e a condição pode produzir um evento sério.

A gravidade tem três valores: baixa, média e alta. Escolha pensando na consequência do evento, e não na sua reação a ele. Um susto grande pode ser um evento de gravidade baixa, e uma anomalia que ninguém notou pode ser de gravidade alta.

O sistema não calcula gravidade e não sugere gravidade. Classificar gravidade é ato de uma pessoa, com consequência para quem vai ler o registro depois, e por isso o campo é preenchido por quem registra e revisto por quem trata. Nenhuma sugestão automática preenche esse campo sozinha.

Se, ao tratar a ocorrência, ficar claro que a gravidade inicial não correspondia ao que se apurou, registre isso no texto da análise. O que importa para quem ler depois é entender como a leitura mudou, e não que o campo pareça ter estado certo desde o começo.`,

  'voe-sg-05': `## Tratar uma ocorrência: da abertura ao encerramento

Toda ocorrência nasce **aberta**. Tratar é registrar a análise e dizer para onde ela vai.

1. Abra a ocorrência na lista.
2. Leia o relato inteiro, e confira os campos de voo, aeronave e aeródromo. Quando aparecer "sem voo relacionado" ou equivalente, isso significa que aquele vínculo não existe para esta ocorrência, e não que alguém esqueceu de preencher.
3. Clique em **Tratar**.
4. Escreva a **Análise**. É o texto que explica o que foi apurado.
5. Escolha para onde a ocorrência vai: **Em análise** ou **Encerrada**.
6. Salve.

**Por que "Aberta" não aparece como destino.** Quem está escrevendo uma análise já saiu de aberta por definição. Se fosse possível salvar uma análise e deixar o registro parecendo intocado, a próxima pessoa reanalisaria o que já foi analisado, e o trabalho seria feito duas vezes sem ninguém perceber.

**Em análise** é para quando a apuração continua e você quer deixar registrado o que já se sabe. **Encerrada** é para quando a apuração terminou. Não existe prazo automático nem passagem automática entre os estados: quem move a ocorrência é sempre uma pessoa, e a data e o autor do tratamento ficam gravados junto.

O texto da análise fica visível no detalhe, ao lado do relato original. O relato não é alterado pelo tratamento: ele continua como quem registrou escreveu. Isso é proposital, porque a comparação entre o que foi relatado e o que foi apurado é justamente o que a leitura posterior precisa enxergar.`,

  'voe-sg-06': `## Encontrar uma ocorrência na lista

A tela de Segurança abre com a lista de ocorrências e um filtro por situação, com quatro opções:

- **Todas**, sem recorte.
- **Aberta**, o que ainda não foi tratado.
- **Em análise**, o que está sendo apurado.
- **Encerrada**, o que já foi concluído.

O filtro de **Aberta** é o mais usado no dia a dia, porque a pergunta de quem cuida da segurança operacional costuma ser o que ainda não foi olhado por ninguém.

Cada linha da lista mostra o título, o tipo e a situação. Ao abrir uma ocorrência, o detalhe traz os três classificadores juntos numa linha só: o tipo, a gravidade e a situação. Eles ficam lado a lado porque se leem juntos, e a pergunta de quem abre um registro é sempre a mesma sequência: o que foi, quão grave, e em que pé está.

No celular, essa linha quebra sozinha conforme a largura, e a leitura continua na mesma ordem.

**Se a lista aparecer vazia**, confira antes o filtro selecionado: um recorte por Encerrada numa operação que ainda não encerrou nada mostra uma lista vazia legítima. Lista vazia por filtro e falha de carregamento são coisas diferentes, e a tela avisa quando o problema é de carregamento, com a mensagem do servidor.`,

  'voe-cf-01': `## Item obrigatório trava a conclusão do checklist

Todo item de checklist é obrigatório ou opcional, e essa marca não é etiqueta informativa: ela decide se a execução pode ser concluída.

**Item obrigatório:** enquanto ele não for respondido, a conclusão não acontece.

**Item opcional:** pode ficar sem resposta, e a execução conclui assim mesmo.

Repare que responder **não** é responder. Um item obrigatório marcado como não conforme não trava nada, porque alguém olhou e constatou. O que trava é o item obrigatório que ninguém respondeu.

A tela diz o que falta **antes** de você clicar em concluir, e não depois. Os itens obrigatórios pendentes aparecem nomeados um a um, ao lado do botão, e em ordem. Isso existe porque um botão que só recusa no fim obriga a rolar a lista inteira procurando o que ficou para trás, e num checklist de quarenta itens é exatamente isso que leva alguém a responder qualquer coisa só para destravar.

Se um item obrigatório não pode ser verificado naquela execução, não invente resposta. Deixe a execução salva em andamento, resolva o que impede a verificação, e conclua depois. Um checklist concluído com resposta inventada vale menos que um checklist em aberto, porque afirma que algo foi verificado quando não foi.

Quem decide que um item é obrigatório é quem cadastrou o checklist, e não quem executa. Se um item obrigatório não faz sentido para o tipo de operação que você faz, isso é assunto de revisão do checklist, e não de contorno na hora da execução.`,

  'voe-cf-02': `## Executar um checklist: responder, salvar e concluir

1. Abra **Conformidade** no menu.
2. Escolha o checklist na lista. O detalhe mostra o código, o título, o regime quando houver e a norma vinculada quando houver.
3. Percorra os itens na ordem em que aparecem.
4. Para cada item, escolha **Sim** ou **Não**.
5. Quando terminar, use **Salvar** para guardar o andamento, ou **Concluir** para fechar a execução.

**Salvar e concluir são gestos diferentes.** Salvar guarda o que você respondeu até ali e deixa a execução em andamento. Concluir fecha a execução. Você pode parar no meio, salvar, e voltar depois sem perder nada do que já respondeu.

**Nenhum item vem pré-marcado.** Você vai encontrar cada item sem resposta até tocar em um dos dois botões, e isso é proposital: um checklist que já começa respondido transforma a verificação em confirmação, e a pessoa passa a clicar em concluir sem ler.

**A conclusão só é oferecida com todos os obrigatórios respondidos.** Se faltar algum, a tela nomeia quais são, e você vai direto neles pelo número.

**Se o salvamento falhar**, a mensagem do servidor aparece e as suas respostas continuam na tela. Você não precisa refazer o checklist: corrija o que a mensagem apontar e salve de novo.

Concluir é um ato seu. O sistema não fecha checklist sozinho quando a última resposta chega, e não conclui por tempo. Enquanto você não concluir, a execução fica em andamento, e é assim que ela aparece no histórico.`,

  'voe-cf-03': `## Sim, não e sem resposta são três estados, e não dois

Um item de checklist pode estar em três situações, e confundi-las é o erro clássico de checklist em papel:

- **Sim**: conforme. Alguém verificou e está de acordo.
- **Não**: não conforme. Alguém verificou e constatou que não está de acordo.
- **Sem resposta**: ninguém olhou ainda.

O terceiro não é uma resposta, é a ausência dela. Por isso a tela usa dois botões, e não uma caixa de marcar: uma caixa só tem marcada e desmarcada, e não consegue distinguir "não conforme" de "ninguém olhou". Com dois botões, nenhum aceso significa não respondido, e isso fica visível na linha.

**Não respondido não é não conforme.** Contar o item sem resposta como não conforme inventa uma constatação que ninguém fez. Contá-lo como conforme é pior ainda, porque afirma que algo foi verificado quando não foi.

**Marcar não é o comportamento correto quando o item não está conforme.** Não é falha sua e não atrapalha a execução: um item obrigatório respondido com não permite concluir normalmente. O que o sistema espera de você é a constatação, e não a aprovação.

Registrar uma não conformidade é o que faz o checklist servir para alguma coisa. Um histórico em que tudo é sempre sim não prova que a operação está em ordem: prova que ninguém usou o checklist para dizer o contrário.

Se você marcou uma resposta por engano, toque no outro botão para trocá-la enquanto a execução não estiver concluída.`,

  'voe-cf-04': `## A ordem dos itens do checklist, e por que ela aparece na tela

Cada item de checklist tem uma posição declarada, e essa posição é sempre a mesma em toda execução. O número aparece na tela, à esquerda da descrição.

O número está visível de propósito. Quem executa um checklist conversa por número: "parei no 12", "o 7 está pendente". Esconder o número obriga a contar linhas, e a contagem erra justamente quando a lista é longa.

**A sequência não é decorativa.** Item de verificação externa antes de item de cabine não é preferência de quem escreveu: a inversão muda o que a pessoa consegue observar, porque parte da verificação depende de a anterior já ter sido feita. Execute na ordem apresentada.

A lista de itens obrigatórios pendentes também sai em ordem, pelo mesmo motivo: uma lista de pendências fora de sequência obriga a procurar duas vezes o mesmo item.

Se você precisar pular um item e voltar nele, pode. A ordem organiza a execução, e não impede a navegação. O que a tela cobra ao final são os obrigatórios sem resposta, e não a sequência em que você os respondeu.

Se um item parecer fora de lugar na sequência, isso é assunto de revisão do cadastro do checklist. Não é ajustável durante a execução, e nem deveria ser: se cada pessoa reordenasse os itens ao executar, duas execuções do mesmo checklist deixariam de ser comparáveis.`,

  'voe-cf-05': `## O checklist vinculado a uma norma, e quem avalia a conformidade

Um checklist pode estar vinculado a uma norma, e pode não estar. Os dois casos são legítimos: um checklist interno do operador não precisa se prender a norma nenhuma, e um checklist que existe para atender uma exigência costuma citar a norma que a exige.

Quando há vínculo, o código da norma aparece no detalhe do checklist, exatamente como a autoridade o publica, sem tradução. Ele está ali para você conferir a execução contra o texto que vale, e não como enfeite.

O filtro da tela recorta por esse vínculo: **Com norma vinculada** e **Sem norma vinculada**. É o recorte que quem audita procura primeiro.

**A tela não conclui conformidade sozinha.** Ela não pontua, não calcula percentual e não emite parecer. O resultado de uma execução é o conjunto de respostas que uma pessoa registrou, com a data e o autor. Quem avalia se a operação está conforme é uma pessoa, lendo essas respostas contra a norma.

Isso é proposital. Um número de conformidade calculado automaticamente parece objetivo e esconde quem decidiu o critério. Com as respostas à vista e a norma ao lado, quem audita vê o mesmo que quem executou, e pode discordar com fundamento.

Para o registro servir numa auditoria, o que importa é que ele seja fiel: item verificado marcado como verificado, não conformidade registrada como não conformidade, e item que não pôde ser verificado deixado sem resposta, com a execução em andamento.`,

  'voe-cf-06': `## Consultar execuções anteriores de um checklist

Ao abrir um checklist, o detalhe mostra as execuções já registradas dele. Cada linha do histórico traz:

- **Quando** a execução ocorreu, com data e hora.
- **Quem** executou, quando o autor foi registrado.
- **O voo** a que a execução pertence.
- **Se foi concluída** ou se continua em andamento.

Data e hora aparecem sempre no formato de 24 horas, independentemente do idioma em que você está usando o sistema. Leitura aeronáutica é em 24 horas, e uma máquina configurada em outro idioma não muda isso.

**Concluída e "todas as respostas dadas" não são a mesma coisa.** Uma execução pode ter todos os itens obrigatórios respondidos e ainda estar em andamento, porque concluir é um ato de quem executa. Ao ler o histórico, uma execução em andamento significa que ninguém a deu por encerrada, e não que faltou responder algo.

**Quando o autor aparece vazio**, significa que aquela execução não registrou quem a fez. É uma ausência de dado, e não um erro da tela.

O histórico é o que sustenta a prova de que a operação fez o que fez e quando fez. Por isso execução não se apaga para "limpar" a lista: uma execução com não conformidades registradas vale mais como registro do que uma lista curta e sem contradições.`,

  'voe-nr-01': `## Consultar uma norma na base

1. Abra **Normas** no menu.
2. Use a busca pelo código ou pelo título. O código é o que a autoridade publica, e é assim que ele está cadastrado.
3. Abra a norma na lista.

O detalhe traz:

- **Código e título**, exatamente como publicados.
- **Órgão** emissor. Quando a ficha não o registra, a tela mostra que não foi informado.
- **Escopo**, em uma linha, dizendo a que a norma se aplica. Também pode não estar informado.
- **A ficha**, que é o resumo redigido.
- **O link para o texto oficial**, quando a ficha registra o endereço. Ele abre em outra aba, para você não perder o lugar onde estava lendo.

O filtro da lista recorta por **Com ficha** e **Sem ficha**, porque uma norma sem ficha redigida ajuda pouco quem está consultando.

**A ficha resume, e a fonte oficial é o destino.** Para citar num briefing ou numa auditoria, confira contra o texto oficial pelo link. A ficha existe para você achar rápido e entender o contexto, e não para substituir a publicação.

**Se a ficha trouxer um trecho marcado para verificar**, isso significa que aquele ponto ainda não foi confirmado contra a fonte oficial. Trate o trecho como pendente de conferência, e vá ao texto oficial antes de usá-lo.`,

  'voe-nr-02': `## Por que o texto da norma não é traduzido

O VOE funciona em português e em inglês, e a troca de idioma alcança a tela inteira. Quatro coisas ficam de fora dessa troca, sempre, e isso é decisão e não esquecimento:

- O **código** da norma.
- O **título** oficial.
- O **órgão** emissor.
- O **corpo** da ficha.

Só a moldura em volta é traduzida: rótulos, botões e avisos.

**O motivo é que traduzir uma norma produz um documento que não existe em lugar nenhum, e o apresenta como se fosse o original.** Quem opera cita esse texto num briefing e numa auditoria, e a citação precisa bater com a fonte oficial, palavra por palavra. Uma tradução aproximada de um artigo, ainda que correta em sentido, não é o que a autoridade publicou, e não sustenta uma citação.

Isso vale igual em qualquer idioma de interface. Se você trocar a tela para inglês, o código, o título e o corpo continuam exatamente como estão publicados.

**O mesmo vale para as fontes citadas em respostas de IA.** O código e o trecho aparecem crus, sem passar por tradução, pela mesma razão: eles existem para você conferir contra a publicação.

Se um trecho estiver difícil de entender, o caminho é consultar o texto oficial e, se for o caso, perguntar às normas para receber uma explicação em linguagem corrente. A explicação é apoio; o texto que vale continua sendo o original.`,

  'voe-nr-03': `## Ficha ainda não redigida não é norma sem conteúdo

Ao abrir uma norma, você pode encontrar a mensagem de que a ficha ainda não foi redigida. Ela significa uma coisa específica: a norma está cadastrada no sistema e o resumo dela ainda não foi escrito.

**Não significa que a norma não tem conteúdo, e não significa falha de carregamento.** A tela diz isso com todas as letras em vez de deixar a área em branco, justamente porque espaço vazio na tela leva a duas conclusões erradas: que a norma é curta, ou que algo quebrou.

Nesse caso, o caminho é o texto oficial. Quando a ficha registra o endereço da fonte, o link aparece no cabeçalho e abre em outra aba. Quando não registra, a tela avisa que a ficha não tem esse endereço, e a consulta precisa ser feita na publicação do órgão.

Você pode achar essas normas de uma vez pelo filtro **Sem ficha** na lista. É o recorte útil para quem vai redigir as fichas, e também para quem quer saber de antemão quais consultas vão exigir ir à fonte.

Ficha pendente não impede nada: a norma continua podendo ser vinculada a um checklist e citada normalmente pelo código. O que muda é que a consulta rápida dentro do sistema não está disponível para ela ainda.`,

  'voe-nr-04': `## Perguntar às normas, e o que fazer com a resposta

Na tela de Normas você pode fazer uma pergunta em linguagem corrente e receber uma resposta que cita as normas em que se baseia.

1. Escreva a pergunta no campo de pergunta.
2. Envie.
3. Aguarde a resposta. Enquanto ela não chega, a tela indica que a pergunta está em andamento.

**A resposta vem marcada como sugestão**, com o aviso antes do texto e não depois. Ele fica antes de propósito: no fim de uma resposta longa, o aviso nasceria fora da área visível, e você leria a resposta inteira sem saber que é sugestão.

**A resposta cita as fontes**, logo abaixo do texto, cada uma com o código da norma e o trecho que sustenta a afirmação. O trecho vem junto porque citar apenas o código não permite conferir: a norma inteira tem centenas de parágrafos, e a citação útil aponta o ponto exato.

**Resposta sem fonte não é exibida.** Se a resposta chegar sem citar norma nenhuma, a tela avisa que ela não pode ser mostrada, e explica por quê. Isso não é falha: é o sistema recusando apresentar uma afirmação que ninguém consegue verificar.

**O que fazer com a resposta:** use-a para achar o caminho, e confira contra a norma citada antes de agir. Abra a ficha, e o texto oficial pelo link.

**O que a resposta não faz:** ela não decide sobre o voo, não autoriza operação e não substitui o texto da norma. Esse aviso aparece junto da própria resposta, e não num rodapé distante.

**Se a pergunta falhar**, a mensagem que aparece é a do servidor, sem tradução. Ela é feia de propósito: é a única informação que permite a alguém resolver o problema.`,

  'voe-nr-05': `## Citar uma norma num briefing ou numa auditoria

Quando você cita uma norma, três coisas precisam bater com a publicação oficial: o código, o trecho e o sentido.

1. Abra a norma na base e copie o **código** exatamente como aparece. Ele não é normalizado nem reescrito pelo sistema, e não muda com o idioma da tela.
2. Localize o **trecho** que sustenta o que você vai afirmar. Citar só o código não permite a ninguém conferir, porque a norma inteira tem centenas de parágrafos.
3. Abra o **texto oficial** pelo link da ficha e confira o trecho ali antes de usar.

**A ficha é resumo, e a fonte oficial é o que vale.** Ela existe para você achar rápido e entender o contexto. Uma citação de auditoria feita a partir do resumo, sem passar pela publicação, corre o risco de repetir uma imprecisão da redação da ficha.

**Trecho marcado para verificar não se cita.** Quando a ficha traz uma marca indicando que aquele ponto ainda não foi confirmado contra a fonte, ele é candidato a conferência, e não material de citação.

**Resposta de IA não é fonte.** Ela cita fontes, e são essas fontes que você cita. Levar para um briefing a frase produzida pela IA, em vez do texto da norma, é apresentar como norma algo que não é.

Quando a ficha não registra o endereço oficial, a tela avisa. Nesse caso, a conferência é na publicação do órgão emissor, pelo canal oficial dele.`,

  'voe-ia-01': `## O que a IA faz no VOE

A IA do VOE tem quatro usos, e eles estão escritos na própria tela de entrada do sistema:

**Traduz METAR, TAF e NOTAM para português claro.** O texto original fica sempre ao lado da tradução, e não no lugar dela. Os boletins vêm das fontes oficiais, e o original é o que vale para qualquer decisão.

**Organiza ocorrências.** Ela ajuda a agrupar e a enxergar padrões num conjunto de registros de segurança. Ela não classifica gravidade e não preenche campo de classificação sozinha.

**Ajuda a escrever plano.** Ela apoia a redação, e o conteúdo continua sendo seu.

**Responde sobre norma citando a fonte.** Toda resposta traz as normas e os trechos em que se baseia, e uma resposta sem fonte não chega a ser exibida.

Em todos os quatro casos, a saída aparece marcada como sugestão. Ela é insumo para o seu trabalho, e não um resultado pronto para ser usado sem leitura.

O artigo sobre o que a IA não faz é a outra metade desta lista, e vale ler os dois juntos. O limite é tão parte do recurso quanto a capacidade: saber o que ela não decide é o que permite usar com segurança o que ela faz.`,

  'voe-ia-02': `## O que a IA não faz, e de quem é a decisão

O limite está escrito na tela de entrada do sistema, antes de qualquer uso, e ele é curto:

**Ela não decide se o voo pode sair.**

**Ela não recomenda decolagem.** Não sugere decolar, adiar ou desviar, em nenhuma tela.

**Ela não calcula peso e balanceamento nem jornada.**

**Ela não assina nada.**

**A decisão é do comandante.**

Esse limite não é uma limitação técnica temporária que será removida numa versão futura. É uma decisão sobre o que o sistema faz: ato que gera consequência para alguém fora do sistema é praticado por uma pessoa identificada, com o que a IA produziu à vista e marcado como saída de IA.

Na tela de ocorrências de segurança não existe saída de IA nenhuma. Classificar gravidade de uma ocorrência tem consequência para quem vai ler o registro depois, e por isso o campo é preenchido por pessoas. Se um dia entrar uma sugestão ali, ela entrará marcada como sugestão e com a fonte, e nunca preenchendo o campo sozinha.

Na tela de normas, a resposta traz o aviso junto do próprio texto: ela não decide sobre o voo, não autoriza operação e não substitui a norma.

**O que fazer com isso na prática:** trate toda saída de IA como um insumo que economiza leitura, e nunca como uma conclusão. A tradução de um boletim acelera a leitura, e a decisão continua sendo tomada sobre o texto original. A resposta sobre norma aponta onde olhar, e a citação que vale é a da publicação oficial.`,

  'voe-ia-03': `## Resposta sem fonte citada não é exibida

Quando você pergunta às normas e a resposta chega sem citar norma nenhuma, o sistema não mostra o texto. No lugar dele, aparece um aviso dizendo que a resposta chegou sem citar norma e por isso não é exibida.

**Isso não é falha, e não é a IA se recusando a responder.** É o sistema recusando apresentar uma afirmação que ninguém consegue conferir.

O motivo é simples: uma afirmação sobre norma sem a fonte que a sustenta não pode ser verificada, mesmo quando está certa. Quem lê não tem como distinguir uma resposta correta de uma resposta plausível, e num contexto de operação essa diferença é tudo.

Cada fonte citada traz duas coisas juntas: o **código** da norma e o **trecho** que sustenta a afirmação. O trecho é obrigatório porque citar apenas o código não permite conferir nada: a norma inteira tem centenas de parágrafos, e apontar a norma sem apontar o ponto é o mesmo que não citar.

**O que fazer quando isso acontecer:** consulte a ficha da norma diretamente, pela tela de Normas. O aviso na tela diz exatamente isso, e é o caminho certo.

Se o caso se repetir para o mesmo tipo de pergunta, vale registrar. Pergunta que consistentemente não encontra fonte costuma indicar que o assunto não está coberto pelas normas cadastradas, e isso é informação útil para quem mantém a base.`,

  'voe-ia-04': `## Como reconhecer uma saída de IA na tela

Toda saída de IA no VOE é identificada como tal, e o padrão é sempre o mesmo:

**A marca de sugestão vem antes do texto**, e não depois. Ela avisa que aquilo é sugestão e deve ser conferido contra a norma citada. Fica antes de propósito: no fim de uma resposta longa, a marca nasceria fora da área visível, e você leria tudo sem saber que era sugestão.

**As fontes vêm logo abaixo do texto**, separadas por uma linha, cada uma com o código da norma e o trecho. Sem fontes, a resposta não é desenhada.

**O limite fica junto da resposta**, e não num rodapé distante que ninguém lê. Quem acabou de ler uma resposta é justamente quem precisa saber o que ela não decide.

Nas traduções de boletins meteorológicos e de NOTAM, o texto original aparece ao lado da tradução. O original é a referência; a tradução é apoio de leitura.

**Se você encontrar na tela um conteúdo que parece produzido por IA e não tem essas marcas**, trate como achado e registre. Saída de IA sem identificação é defeito, porque quem lê passa a atribuir a um sistema uma afirmação cuja origem não consegue avaliar.

A regra vale em qualquer tela do produto, e vale também para o que vier a ser acrescentado depois: nenhuma saída de IA entra sem a marca e sem a fonte que a sustenta.`,

  'voe-ia-05': `## A cota mensal de uso da IA

Cada organização tem uma cota mensal própria de uso da IA no VOE, e ela não é compartilhada entre organizações. O uso de uma não consome a cota de outra.

A cota é medida pelo volume de texto processado, somando o que você envia e o que a IA devolve. O valor vigente é de 2 milhões de unidades de texto por mês, por organização. Na prática, isso permite traduzir boletins e organizar ocorrências ao longo do mês inteiro numa operação pequena, e não cobre uso sem limite.

**Por que existe teto.** O acesso à IA é fornecido pelo sistema, e o custo é da plataforma. Sem teto, o uso de uma organização correria sobre a conta de outra pessoa, e ninguém teria como saber quanto está gastando.

**O que consome cota:** traduzir boletins, organizar ocorrências, apoiar a escrita de plano e perguntar às normas.

**O que não consome:** consultar a ficha de uma norma, executar checklist, registrar ocorrência e todo o resto do sistema. As telas funcionam integralmente sem IA.

**Se a cota do mês se esgotar**, os recursos de IA ficam indisponíveis até a virada do mês, e o restante do produto continua funcionando normalmente. Nenhuma operação depende de IA para acontecer: o texto original dos boletins continua acessível, as normas continuam consultáveis, e o registro de ocorrência e a execução de checklist não passam por ela.

O valor da cota pode ser revisto. Quando mudar, o número novo passa a valer para todas as organizações, e este artigo é atualizado junto.`,
};

/**
 * A JUNCAO DOS QUATRO BLOCOS.
 *
 * Eles foram redigidos em paralelo, um por grupo de categorias, cada um lendo as telas do
 * seu proprio assunto. A divisao NAO foi por conveniencia: artigo de meteorologia escrito
 * por quem leu a tela de frota sai generico, e generico e o comeco da ajuda inventada.
 */
const ARTIGOS: readonly ArtigoDaBase[] = [
  ...ARTIGOS_1,
  ...ARTIGOS_2,
  ...ARTIGOS_3,
  ...ARTIGOS_4,
];

/** Os corpos, na mesma juncao. A chave e o id do artigo, e a tela busca por ela ao abrir. */
const CORPOS: Readonly<Record<string, string>> = {
  ...CORPOS_1,
  ...CORPOS_2,
  ...CORPOS_3,
  ...CORPOS_4,
};

/**
 * A CONTAGEM DERIVADA, e ela e o motivo de as categorias nascerem sem o campo `artigos`.
 *
 * `CategoriaAjuda.artigos` documenta: "Quantos artigos PUBLICADOS ela tem. Zero significa
 * que ela nao aparece." Entao a contagem NAO e enfeite: ela decide se a categoria e
 * desenhada. Uma categoria com contagem errada some da tela ou aparece vazia, e nos dois
 * casos a pessoa conclui que a ajuda esta quebrada.
 */
const CATEGORIAS: readonly CategoriaAjuda[] = CATEGORIAS_SEM_CONTAGEM.map((c) => ({
  ...c,
  artigos: ARTIGOS.filter((a) => a.categoriaId === c.id).length,
}));

/**
 * O ACERVO DO NAMESPACE `voe`, no formato que `fonte-memoria.ts` consome.
 *
 * O NAMESPACE E `voe` porque e o que o manifesto declara em `voe.config.ts:120`. Ele NAO e
 * o id do app por coincidencia: e a chave que impede dois produtos de verem a ajuda um do
 * outro, e a suite de conformidade transversal cobra que nenhum namespace se repita entre
 * produtos.
 */
export const ACERVO_VOE: AcervoDaBase = {
  categorias: CATEGORIAS,
  artigos: ARTIGOS,
  corpos: CORPOS,
};
